import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import {
  CreditCard,
  Mail,
  FileText,
  Building,
  Check,
  Calendar,
  ChevronDown,
  Plus,
  User,
  Home,
  Loader,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  X,
  Trash,
  List,
  Clock,
  Users,
  Download,
  Receipt,
  FolderOpen,
  ExternalLink,
  FileSpreadsheet,
  MessageSquare,
  Upload,
  Menu,
  MoreVertical,
  Printer
} from "lucide-react";
import { getListUsers } from '../../Fetch/fetchUser';
import {
  addPaymentVaad,
  getListPaymentsByBuildingId,
  UpdatePaymentVaad,
  getListVaadMonthlyByBuildingId,
  generateWhatsAppMessage,
  testDownloadUrl,
  validateDownloadUrl,
  checkOneTimePayment
} from '../../Fetch/fetchPayment';

import {
  generatePaymentReceipt,
  downloadPaymentReceipt,
  viewPaymentReceiptInNewTab,
  fetchPaymentReceipts,
  viewEmailContentInNewTab,
  downloadEmailContentFile
} from '../../Fetch/fetchUploadImage';
import { sendWhatsAppMessage } from '../../utils/whatsappUtils';
import PaymentExcelUpload from './PaymentExcelUpload';
import PaymentStatusOverview from './PaymentStatusOverview';
import styles from "./Payment.module.css";

// Action Menu Modal Component (replaces dropdown)
const ActionMenu = ({ group, onViewDetails, onEditPayment, onViewReceipt, onViewEmail, onWhatsAppShare, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      label: '👁️ צפה בפרטים',
      onClick: () => onViewDetails(group),
      disabled: loading
    },
    {
      label: '✏️ ערוך תשלום',
      onClick: () => onEditPayment(group),
      disabled: loading
    }
  ];

  // Add conditional actions
  if (group.hasReceipts) {
    actions.push({
      label: '🧾 צפה בקבלות',
      onClick: () => onViewReceipt(group),
      disabled: loading
    });
  }

  if (group.hasEmailFiles) {
    actions.push({
      label: '📧 צפה בקבצי אימייל',
      onClick: () => onViewEmail(group),
      disabled: loading
    });
  }

  actions.push({
    label: '📱 שלח ב-WhatsApp',
    onClick: () => onWhatsAppShare(group),
    disabled: loading
  });

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Payment Method Badge Component
const PaymentMethodBadge = ({ method, className = "" }) => {
  const getBadgeConfig = (paymentMethod) => {
    switch (paymentMethod) {
      case "מזומן":
      case "Cash":
        return {
          text: "מזומן",
          icon: <span className={styles.badgeIcon}>₪</span>,
          className: styles.paymentMethodCash
        };
      case "צ'ק":
        return {
          text: "צ'ק",
          icon: <FileText className={styles.badgeIcon} />,
          className: styles.paymentMethodCheck
        };
      case "העברה בנקאית":
        return {
          text: "העברה בנקאית",
          icon: <CreditCard className={styles.badgeIcon} />,
          className: styles.paymentMethodBankTransfer
        };
      case "Credit Card":
        return {
          text: "כרטיס אשראי",
          icon: <CreditCard className={styles.badgeIcon} />,
          className: styles.paymentMethodCreditCard
        };
      default:
        return {
          text: method,
          icon: <span className={styles.badgeIcon}>₪</span>,
          className: styles.paymentMethodCash
        };
    }
  };

  const config = getBadgeConfig(method);

  return (
    <span className={`${styles.paymentMethodBadge} ${config.className} ${className}`}>
      {config.icon}
      {config.text}
    </span>
  );
};

// Updated PaymentCard Component
const PaymentCard = ({
  group,
  onViewDetails,
  onEditPayment,
  onViewReceipt,
  onViewEmail,
  onWhatsAppShare,
  formatAmount,
  formatMonthsNumeric
}) => (
  <div className={styles.dataCard}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        <Building size={16} className={styles.cardIcon} />
        <span>דירה {group.apartmentNumber} - {group.year}</span>
      </div>
      <span className={styles.monthsBadge}>
        {formatMonthsNumeric(group.months, `card-${group.apartmentNumber}-${group.year}`)}
      </span>
    </div>
    <div className={styles.cardBody}>
      <div className={styles.cardInfo}>
        <User size={14} />
        <span>{group.tenantName || group.tenantEmail}</span>
      </div>
      <div className={styles.cardInfo}>
        <Mail size={14} />
        <span>{group.tenantEmail}</span>
      </div>
      <div className={styles.cardInfo}>
        <span style={{fontSize: '14px'}}>₪</span>
        <span>סה״כ: {formatAmount(group.totalAmount)}</span>
      </div>
      <div className={styles.cardInfo}>
        <Calendar size={14} />
        <span>שנה: {group.year}</span>
      </div>
      <div className={styles.cardInfo}>
        <CreditCard size={14} />
        <div className={styles.paymentMethodsContainer}>
          {group.paymentMethods.map((method, idx) => (
            <PaymentMethodBadge key={idx} method={method} />
          ))}
        </div>
      </div>
      <div className={styles.cardActions}>
        <button
          className={styles.actionButton}
          title="צפה בפרטים"
          onClick={() => onViewDetails(group)}
        >
          <Eye size={16} />
        </button>
        <button
          className={styles.actionButton}
          title="ערוך תשלום אחרון"
          onClick={() => onEditPayment(group)}
        >
          <Edit size={16} />
        </button>
        {group.hasReceipts && (
          <button
            className={`${styles.actionButton} ${styles.receiptButton}`}
            title="צפה בקבלות"
            onClick={() => onViewReceipt(group)}
          >
            <Receipt size={16} />
          </button>
        )}
        {group.hasEmailFiles && (
          <button
            className={`${styles.actionButton} ${styles.emailButton}`}
            title="צפה בקבצי אימייל"
            onClick={() => onViewEmail(group)}
          >
            <Mail size={16} />
          </button>
        )}
        <button
          className={`${styles.actionButton} ${styles.whatsappButton}`}
          title="שלח ב-WhatsApp"
          onClick={() => onWhatsAppShare(group)}
        >
          <MessageSquare size={16} />
        </button>
      </div>
    </div>
  </div>
);

// Mobile PaymentCard Component
const MobilePaymentCard = ({
  group,
  onViewDetails,
  onEditPayment,
  onViewReceipt,
  onViewEmail,
  onWhatsAppShare,
  formatAmount,
  formatMonthsNumeric
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const getInitials = (name) => {
    if (!name) return 'TP';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleViewDetails = () => {
    setShowMenu(false);
    onViewDetails(group);
  };

  const handleEdit = () => {
    setShowMenu(false);
    onEditPayment(group);
  };

  const handleViewReceipt = () => {
    setShowMenu(false);
    onViewReceipt(group);
  };

  const handleViewEmail = () => {
    setShowMenu(false);
    onViewEmail(group);
  };

  const handleWhatsApp = () => {
    setShowMenu(false);
    onWhatsAppShare(group);
  };

  return (
    <div className={styles.mobilePaymentCard}>
      {/* Menu Button - Top Right */}
      <div className={styles.menuContainer}>
        <button className={styles.menuBtn} onClick={() => setShowMenu(!showMenu)}>
          <MoreVertical size={20} />
        </button>
        {showMenu && (
          <>
            <div className={styles.menuOverlay} onClick={() => setShowMenu(false)} />
            <div className={styles.menuDropdown}>
              <button className={styles.menuItem} onClick={handleViewDetails}>
                <Eye size={16} />
                <span>צפה בפרטים</span>
              </button>
              <button className={styles.menuItem} onClick={handleEdit}>
                <Edit size={16} />
                <span>ערוך</span>
              </button>
              {group.hasReceipts && (
                <button className={styles.menuItem} onClick={handleViewReceipt}>
                  <Receipt size={16} />
                  <span>קבלות</span>
                </button>
              )}
              {group.hasEmailFiles && (
                <button className={styles.menuItem} onClick={handleViewEmail}>
                  <Mail size={16} />
                  <span>אימייל</span>
                </button>
              )}
              <button className={styles.menuItem} onClick={handleWhatsApp}>
                <MessageSquare size={16} />
                <span>WhatsApp</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Avatar - Top Left with CreditCard icon */}
      <div className={styles.paymentAvatar}>
        <CreditCard size={36} className={styles.avatarIcon} />
      </div>

      {/* Payment Info */}
      <div className={styles.paymentInfo}>
        <h2 className={styles.paymentName}>{group.tenantName || group.tenantEmail}</h2>
        <p className={styles.paymentDetails}>דירה {group.apartmentNumber} | {group.year}</p>
      </div>

      {/* Months Display */}
      <div className={styles.paymentMonths}>
        {formatMonthsNumeric(group.months, `mobile-card-${group.apartmentNumber}-${group.year}`)}
      </div>

      {/* Stats */}
      <div className={styles.paymentStats}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>סה"כ</div>
          <div className={styles.statValue}>{formatAmount(group.totalAmount)}</div>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>אמצעי תשלום</div>
          <div className={styles.statValue}>
            {group.paymentMethods.map((method, idx) => (
              <PaymentMethodBadge key={idx} method={method} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


export default function PaymentPage() {
  // Existing state variables...
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [currentView, setCurrentView] = useState("list");
  const [payments, setPayments] = useState([]);
  const [groupedPayments, setGroupedPayments] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApartmentFilter, setSelectedApartmentFilter] = useState(""); // For apartment number filtering
  const [showOneTimePaymentsOnly, setShowOneTimePaymentsOnly] = useState(false); // Filter for one-time payments
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false); // Filter for unpaid payments
  const [showUnpaidOneTimeOnly, setShowUnpaidOneTimeOnly] = useState(false); // Filter for users who haven't paid one-time payment
  const [showMobileSearch, setShowMobileSearch] = useState(false); // Mobile search modal
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paymentsError, setPaymentsError] = useState("");
  const [vaadMonthlyData, setVaadMonthlyData] = useState({});
  const [isLoadingVaadMonthly, setIsLoadingVaadMonthly] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false); // For Excel upload modal
  const [showStatusOverview, setShowStatusOverview] = useState(false); // For payment status overview
  const [vaadMonthlyError, setVaadMonthlyError] = useState("");
  const [alreadyPaidMonths, setAlreadyPaidMonths] = useState([]);

  // Receipt-related state
  const [receipts, setReceipts] = useState([]);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(false);
  const [receiptsError, setReceiptsError] = useState("");
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [selectedGroupForReceipt, setSelectedGroupForReceipt] = useState(null);

  // Email file-related state
  const [emailFiles, setEmailFiles] = useState([]);
  const [isLoadingEmailFiles, setIsLoadingEmailFiles] = useState(false);
  const [emailFilesError, setEmailFilesError] = useState("");
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedGroupForEmail, setSelectedGroupForEmail] = useState(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  // WhatsApp dropdown state for unpaid one-time users
  const [isWhatsappOneTimeDropdownOpen, setIsWhatsappOneTimeDropdownOpen] = useState(false);
  const whatsappOneTimeDropdownRef = useRef(null);

  // Mobile dropdown state
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const mobileDropdownRef = useRef(null);
  
  // Search input ref for auto-focus
  const tenantSearchInputRef = useRef(null);

  // Form state variables
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPaymentForView, setSelectedPaymentForView] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [originalPaymentData, setOriginalPaymentData] = useState(null);
  // Payment type is now fixed to "ועד בית"
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [completedPaymentData, setCompletedPaymentData] = useState(null);
  const [additionalPaymentDetails, setAdditionalPaymentDetails] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] = useState(false);

  // Updated tenant/apartment fields
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantId, setTenantId] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [buildingId, setBuildingId] = useState("");

  // Month selection
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [paymentYear, setPaymentYear] = useState(new Date().getFullYear().toString());
  const [paymentType, setPaymentType] = useState("regular"); // "regular" or "one_time"

  // One-time payment duplicate warning state
  const [showOneTimeWarning, setShowOneTimeWarning] = useState(false);
  const [existingOneTimePayments, setExistingOneTimePayments] = useState([]);

  // Users dropdown
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // Payment methods array - NEW
  const paymentMethods = [
    { id: "cash", name: "מזומן" },
    { id: "check", name: "צ'ק" },
    { id: "bank_transfer", name: "העברה בנקאית" }
  ];

  // Helper function to get payment method name
  const getPaymentMethodName = (methodId) => {
    return paymentMethods.find(method => method.id === methodId)?.name || "מזומן";
  };


  // Payment type is fixed - no need for array

  const months = [
    { value: "01", label: "ינואר" },
    { value: "02", label: "פברואר" },
    { value: "03", label: "מרץ" },
    { value: "04", label: "אפריל" },
    { value: "05", label: "מאי" },
    { value: "06", label: "יוני" },
    { value: "07", label: "יולי" },
    { value: "08", label: "אוגוסט" },
    { value: "09", label: "ספטמבר" },
    { value: "10", label: "אוקטובר" },
    { value: "11", label: "נובמבר" },
    { value: "12", label: "דצמבר" },
  ];

  // UPDATED: Excel Export Function
  const exportToExcel = async (exportAllPages = false) => {
    try {
      setIsExporting(true);

      // Dynamic import of SheetJS
      const XLSX = await import('xlsx');

      // Determine which data to export
      const dataToExport = exportAllPages ? filteredGroupedPayments : currentGroupedPayments;

      if (dataToExport.length === 0) {
        alert('אין נתונים לייצוא');
        return;
      }

      // Prepare data for Excel
      const excelData = dataToExport.map((group, index) => ({
        'מספר רשומה': index + 1,
        'שנה': group.year,
        'שם דייר': group.tenantName || group.tenantEmail,
        'דירה': group.apartmentNumber + (group.buildingNumber ? ` (בניין ${group.buildingNumber})` : ''),
        'חודשים שולמו': formatMonthsString(group.months),
        'סה״כ סכום': group.totalAmount,
        'אמצעי תשלום': group.paymentMethods.join(', '),
        'יש קבלות': group.hasReceipts ? 'כן' : 'לא',
        'יש קבצי אימייל': group.hasEmailFiles ? 'כן' : 'לא',
        'אימייל דייר': group.tenantEmail,
        'מספר תשלומים': group.paymentDetails.length
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // מספר רשומה
        { wch: 8 },  // שנה
        { wch: 20 }, // שם דייר
        { wch: 15 }, // דירה
        { wch: 15 }, // חודשים שולמו
        { wch: 12 }, // סה״כ סכום
        { wch: 20 }, // אמצעי תשלום
        { wch: 10 }, // יש קבלות
        { wch: 12 }, // יש קבצי אימייל
        { wch: 25 }, // אימייל דייר
        { wch: 12 }  // מספר תשלומים
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      const currentDate = new Date().toLocaleDateString('he-IL');
      const sheetName = exportAllPages
        ? `תשלומים_כל_הנתונים_${currentDate}`
        : `תשלומים_עמוד_${currentPage}_${currentDate}`;

      XLSX.utils.book_append_sheet(wb, ws, 'תשלומים');

      // Generate filename
      const filename = exportAllPages
        ? `תשלומים_כל_הנתונים_${currentDate.replace(/\//g, '-')}.xlsx`
        : `תשלומים_עמוד_${currentPage}_${currentDate.replace(/\//g, '-')}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      // Show success message
      const exportedCount = dataToExport.length;
      const message = exportAllPages
        ? `יוצאו בהצלחה ${exportedCount} רשומות מכל הדפים לקובץ אקסל`
        : `יוצאו בהצלחה ${exportedCount} רשומות מעמוד ${currentPage} לקובץ אקסל`;

      alert(message);

    } catch (error) {
      alert('שגיאה בייצוא לאקסל. אנא נסה שוב.');
    } finally {
      setIsExporting(false);
    }
  };

  const groupPaymentsByApartmentAndYear = (payments) => {
    const grouped = {};

    payments.forEach(payment => {
      const apartmentNumber = payment.apartmentNumber || payment.ApartmentNumber;
      const date = new Date(payment.date || payment.Date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      const key = `${apartmentNumber}-${year}`;

      if (!grouped[key]) {
        grouped[key] = {
          apartmentNumber: apartmentNumber,
          year: year,
          tenantName: payment.tenantName || payment.TenantName,
          tenantEmail: payment.tenantEmail || payment.TenantEmail,
          tenantEmails: new Set(), // Track all emails for this apartment
          buildingNumber: payment.buildingNumber || payment.BuildingNumber,
          months: new Set(),
          totalAmount: 0,
          paymentMethods: new Set(),
          paymentTypes: new Set(),
          paymentDetails: [],
          hasReceipts: false,
          hasEmailFiles: false
        };
      }
      
      // Add this payment's email to the set of emails
      const emailToAdd = payment.tenantEmail || payment.TenantEmail;
      if (emailToAdd) {
        grouped[key].tenantEmails.add(emailToAdd.toLowerCase());
      }

      grouped[key].months.add(month);
      grouped[key].totalAmount += payment.amount || payment.Amount || 0;
      grouped[key].paymentMethods.add(payment.paymentMethod || payment.PaymentMethod);
      grouped[key].paymentTypes.add(payment.paymentType || payment.PaymentType);
      grouped[key].paymentDetails.push(payment);

      if (payment.receiptFilePath || payment.ReceiptFilePath) {
        grouped[key].hasReceipts = true;
      }

      if (payment.emailFilePath || payment.EmailFilePath) {
        grouped[key].hasEmailFiles = true;
      }
    });

    return Object.values(grouped)
      .map(group => ({
        ...group,
        months: Array.from(group.months).sort((a, b) => a - b),
        paymentMethods: Array.from(group.paymentMethods),
        paymentTypes: Array.from(group.paymentTypes),
        tenantEmails: Array.from(group.tenantEmails || new Set())
      }))
      .sort((a, b) => {
        if (a.apartmentNumber !== b.apartmentNumber) {
          return parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber);
        }
        return b.year - a.year;
      });
  };

  const calculateAlreadyPaidMonths = (tenantEmail, apartmentNum, year, currentPaymentType = null) => {
    if (!apartmentNum || !year || !groupedPayments.length) {
      return [];
    }

    const matchingGroup = groupedPayments.find(group => {
      const apartmentMatch = group.apartmentNumber && apartmentNum &&
        group.apartmentNumber.toString() === apartmentNum.toString();

      const yearMatch = group.year && year &&
        group.year.toString() === year.toString();

      // First, check if apartment and year match
      if (!apartmentMatch || !yearMatch) {
        return false;
      }

      // If apartment and year match, now consider email logic
      const hasSearchEmail = tenantEmail && tenantEmail.trim() !== '';
      const hasGroupEmail = group.tenantEmail && group.tenantEmail.trim() !== '';
      const hasGroupEmails = group.tenantEmails && group.tenantEmails.length > 0;

      // Email matching logic - prioritize apartment matching
      let emailMatch = true;

      if (hasSearchEmail && (hasGroupEmail || hasGroupEmails)) {
        // Both search and group have email info - try to match
        if (hasGroupEmails) {
          // Check if searching email is in the array of emails for this apartment
          emailMatch = group.tenantEmails.some(email =>
            email.toLowerCase() === tenantEmail.toLowerCase()
          );
        } else if (hasGroupEmail) {
          // Fallback to single email check
          emailMatch = group.tenantEmail.toLowerCase() === tenantEmail.toLowerCase();
        }
      }
      // If group has no email info, match by apartment/year only (emailMatch stays true)
      // If we're not searching with email, match by apartment/year only (emailMatch stays true)

      const overallMatch = apartmentMatch && yearMatch && emailMatch;

      return overallMatch;
    });

    if (matchingGroup && matchingGroup.paymentDetails) {
      // Filter payments by payment type if specified
      // For "regular" (ועד בית) - only count non-one-time payments
      // For "one_time" (חד פעמי) - only count one-time payments
      const filteredPayments = matchingGroup.paymentDetails.filter(payment => {
        const paymentTypeValue = payment.paymentType || payment.PaymentType || '';
        const isOneTimePayment = paymentTypeValue.includes('חד פעמי');

        if (currentPaymentType === 'one_time') {
          return isOneTimePayment;
        } else if (currentPaymentType === 'regular') {
          return !isOneTimePayment;
        }
        // If no payment type specified, return all (backward compatibility)
        return true;
      });

      // Extract months from filtered payments
      const paidMonths = new Set();
      filteredPayments.forEach(payment => {
        const paymentDate = payment.date || payment.Date;
        if (paymentDate) {
          const date = new Date(paymentDate);
          const month = String(date.getMonth() + 1).padStart(2, '0');
          paidMonths.add(month);
        }
      });

      const result = Array.from(paidMonths);
      return result;
    }

    // Fallback to old behavior if no paymentDetails
    if (matchingGroup && matchingGroup.months) {
      return matchingGroup.months.map(month => String(month).padStart(2, '0'));
    }

    return [];
  };

  // Handle receipt viewing for a group
  const handleViewReceipt = async (groupedPayment) => {
    setSelectedGroupForReceipt(groupedPayment);
    setReceiptModalVisible(true);
    setIsLoadingReceipts(true);
    setReceiptsError("");

    try {
      const groupReceipts = groupedPayment.paymentDetails
        .filter(payment => payment.receiptFilePath || payment.ReceiptFilePath)
        .map(payment => ({
          paymentId: payment.id || payment.paymentId || payment.Id || payment.PaymentId,
          fileName: payment.receiptFileName || payment.ReceiptFileName,
          filePath: payment.receiptFilePath || payment.ReceiptFilePath,
          amount: payment.amount || payment.Amount,
          date: payment.date || payment.Date,
          paymentType: payment.paymentType || payment.PaymentType
        }));

      setReceipts(groupReceipts);
    } catch (error) {
      setReceiptsError('שגיאה בטעינת הקבלות');
    } finally {
      setIsLoadingReceipts(false);
    }
  };

  const calculateTotalAmount = () => {
    const newMonthsCount = getNewlySelectedMonths().length;
    const monthlyAmount = parseFloat(amount) || 0;
    return monthlyAmount * newMonthsCount;
  };

  const handleCloseReceiptModal = () => {
    setReceiptModalVisible(false);
    setSelectedGroupForReceipt(null);
    setReceipts([]);
    setReceiptsError("");
  };

  // Handle email file viewing for a group
  const handleViewEmail = async (groupedPayment) => {
    setSelectedGroupForEmail(groupedPayment);
    setEmailModalVisible(true);
    setIsLoadingEmailFiles(true);
    setEmailFilesError("");

    try {
      const groupEmailFiles = groupedPayment.paymentDetails
        .filter(payment => payment.emailFilePath || payment.EmailFilePath)
        .map(payment => ({
          paymentId: payment.id || payment.paymentId || payment.Id || payment.PaymentId,
          fileName: payment.emailFileName || payment.EmailFileName,
          filePath: payment.emailFilePath || payment.EmailFilePath,
          generatedAt: payment.emailGeneratedAt || payment.EmailGeneratedAt,
          amount: payment.amount || payment.Amount,
          date: payment.date || payment.Date,
          paymentType: payment.paymentType || payment.PaymentType,
          referenceNumber: payment.referenceNumber || payment.ReferenceNumber
        }));

      setEmailFiles(groupEmailFiles);
    } catch (error) {
      setEmailFilesError('שגיאה בטעינת קבצי האימייל');
    } finally {
      setIsLoadingEmailFiles(false);
    }
  };

  const handleCloseEmailModal = () => {
    setEmailModalVisible(false);
    setSelectedGroupForEmail(null);
    setEmailFiles([]);
    setEmailFilesError("");
  };

  // 🚀 FIXED: Simple WhatsApp share using Dashboard approach
  const handleWhatsAppShare = (groupedPayment) => {
    try {
      // Create simple payment summary message (like Dashboard)
      const payments = groupedPayment.paymentDetails || [];
      const months = groupedPayment.months || [];
      const apartmentNumber = groupedPayment.apartmentNumber;
      const totalAmount = groupedPayment.totalAmount || payments.reduce((sum, p) => sum + (p.Amount || p.amount || 0), 0);
      const year = groupedPayment.year;

      // Determine payment type text
      const paymentTypeText = groupedPayment.paymentType === "one_time" ? "תשלום חד פעמי" : "תשלום ועד בית";

      // Create short, clean message (exactly like Dashboard approach)
      let whatsappMessage = `📄 קבלה על ${paymentTypeText} - דירה ${apartmentNumber}\n`;
      whatsappMessage += `💰 סה"כ: ₪${totalAmount.toLocaleString()}\n`;
      whatsappMessage += `📊 ${payments.length} תשלומים`;
      
      if (months.length > 0) {
        whatsappMessage += ` (${formatMonthsString(months)}${year ? ` - ${year}` : ''})`;
      } else if (year) {
        whatsappMessage += ` (${year})`;
      }
      
      whatsappMessage += `\n\n`;
      
      // Add payment details (keep it short)
      if (payments.length <= 3) {
        // For 3 or fewer payments, show details
        payments.forEach(payment => {
          const paymentAmount = payment.Amount || payment.amount || 0;
          const tenantName = payment.TenantName || payment.tenantName || payment.fullName || payment.name || 'דייר';
          whatsappMessage += `• ${tenantName}: ₪${paymentAmount.toLocaleString()}\n`;
        });
      }
      
      whatsappMessage += `\nתודה על התשלומים! 🙏`;

      // Get phone number (same logic as Dashboard)
      const usersWithPhone = payments
        .filter(payment => payment.phone && payment.phone.trim() !== '')
        .map(payment => ({ 
          name: payment.fullName || payment.name || payment.TenantName || payment.tenantName || 'לא צוין', 
          phone: payment.phone 
        }));

      let phoneNumber = null;
      let successMessage;

      if (usersWithPhone.length > 0) {
        const targetUser = usersWithPhone[0];
        phoneNumber = targetUser.phone;
        successMessage = `📱 WhatsApp נפתח לשליחה ל${targetUser.name} (${targetUser.phone})`;
        if (usersWithPhone.length > 1) {
          successMessage += ` (+${usersWithPhone.length - 1} דיירים נוספים)`;
        }
      } else if (groupedPayment.tenantPhone && groupedPayment.tenantPhone.trim() !== '') {
        phoneNumber = groupedPayment.tenantPhone;
        successMessage = `📱 WhatsApp נפתח לשליחה ל${groupedPayment.tenantName} (${phoneNumber})`;
      } else {
        successMessage = `📱 WhatsApp נפתח - בחר איש קשר לשליחה`;
      }

      // 🎯 KEY: Use the SAME working function as Dashboard!
      const whatsappResult = sendWhatsAppMessage(whatsappMessage, phoneNumber);

      // Show result (same as Dashboard)
      if (whatsappResult) {
        setTimeout(() => {
          alert(`✅ הודעת WhatsApp נוצרה בהצלחה!\n\n${successMessage}`);
        }, 500);
      } else {
        alert('⚠️ לא ניתן לפתוח WhatsApp. בדוק הגדרות הדפדפן.');
      }

    } catch (error) {
      console.error('❌ WhatsApp Share Error:', error);
      alert(`❌ שגיאה ביצירת הודעת WhatsApp: ${error.message}`);
    }
  };

    

    







  // Check for existing one-time payments before submitting
  const checkExistingOneTimePayments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingIdValue = userData?.buildingId;

      if (!buildingIdValue || !apartmentNumber) {
        return { exists: false };
      }

      const result = await checkOneTimePayment(buildingIdValue, apartmentNumber);
      return result;
    } catch (error) {
      console.error('Error checking one-time payments:', error);
      return { exists: false };
    }
  };

  // Handle pre-submit validation for one-time payments
  const handlePreSubmit = async () => {
    // Only check for one-time payments (not in edit mode)
    if (paymentType === "one_time" && !editingPaymentId) {
      setIsProcessing(true);
      const existingResult = await checkExistingOneTimePayments();
      setIsProcessing(false);

      if (existingResult.exists && existingResult.payments && existingResult.payments.length > 0) {
        // Show warning dialog with existing payment details
        setExistingOneTimePayments(existingResult.payments);
        setShowOneTimeWarning(true);
        return; // Don't submit yet, wait for user confirmation
      }
    }

    // No existing one-time payments or not a one-time payment, proceed with submit
    handleSubmit();
  };

  // Confirm and proceed with one-time payment after user acknowledges warning
  const handleConfirmOneTimePayment = () => {
    setShowOneTimeWarning(false);
    setExistingOneTimePayments([]);
    handleSubmit();
  };

  // Cancel one-time payment submission
  const handleCancelOneTimePayment = () => {
    setShowOneTimeWarning(false);
    setExistingOneTimePayments([]);
  };

  // UPDATED: handleSubmit - creates separate payment record for each month
  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;

      // For one-time payments, use selectedMonths directly
      // For regular payments, use getNewlySelectedMonths() to filter out already paid months
      const newMonthsToPayFor = paymentType === "one_time" ? selectedMonths : getNewlySelectedMonths();

      if (newMonthsToPayFor.length === 0) {
        alert('אנא בחר לפחות חודש אחד חדש לתשלום');
        setIsProcessing(false);
        return;
      }

      const monthlyAmount = parseFloat(amount);
      const totalAmount = monthlyAmount * newMonthsToPayFor.length;

      // Array to store successful payment results
      const successfulPayments = [];
      const failedPayments = [];

      // Loop through each selected month and create individual payment records
      for (const month of newMonthsToPayFor) {
        const monthNumber = parseInt(month);

        const paymentData = {
          BuildingId: buildingId,
          ApartmentNumber: apartmentNumber,
          BuildingNumber: buildingNumber,
          Date: new Date(`${paymentYear}-${month.padStart(2, '0')}-01`).toISOString(),
          Amount: monthlyAmount,
          PaymentMethod: getPaymentMethodName(paymentMethod),
          PaymentType: paymentType === "one_time" ? "חד פעמי" : "ועד בית",
          AdditionalDetails: additionalPaymentDetails,
          CheckNumber: paymentMethod === "check" ? checkNumber : null,
          TenantEmail: email,
          // ADD THIS LINE - Get user ID from selected tenant
          TenantId: selectedTenant?.userId || selectedTenant?.UserID || selectedTenant?.userID || null,
          CardLastFour: null,
          PaymentYear: parseInt(paymentYear),
          PaymentMonth: monthNumber,
          Months: [month],
          SelectedMonths: [month]
        };


        try {
          const response = await addPaymentVaad(paymentData);

          if (response.success) {
            // Try to get payment ID from various possible fields
            const paymentId = response.paymentId || response.PaymentId || response.id || response.Id || response.payment_id;

            successfulPayments.push({
              month: monthNumber,
              paymentId: paymentId,
              response: response
            });

            // Generate receipt for this payment if paymentId exists
            if (paymentId) {
              try {
                await generatePaymentReceipt(
                  paymentId,
                  userData?.email || 'info@vaad.com',
                  'ועד הבית',
                  userData?.buildingAddress || ''
                );
              } catch (receiptError) {
              }
            }
          } else {
            failedPayments.push({
              month: monthNumber,
              error: response.message || 'Payment failed'
            });
          }
        } catch (error) {
          failedPayments.push({
            month: monthNumber,
            error: error.message || 'Unknown error'
          });
        }
      }

      setIsProcessing(false);

      // Show results based on success/failure
      if (successfulPayments.length > 0 && failedPayments.length === 0) {
        // All payments successful
        // Show success animation immediately
        setShowSuccessAnimation(true);
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 2000);

        // Store payment data for WhatsApp sharing
        setCompletedPaymentData({
          paymentDetails: successfulPayments.map(p => ({
            id: p.paymentId || p.response?.paymentId,
            paymentId: p.paymentId || p.response?.paymentId,
            Id: p.paymentId || p.response?.paymentId,
            PaymentId: p.paymentId || p.response?.paymentId,
            month: p.month,
            year: paymentYear,
            amount: monthlyAmount,
            tenantName: selectedTenant?.fullName,
            TenantName: selectedTenant?.fullName,
            paymentMethod: getPaymentMethodName(paymentMethod),
            PaymentMethod: getPaymentMethodName(paymentMethod)
          })),
          months: successfulPayments.map(p => p.month), // Use actual paid months, not selectedMonths
          year: paymentYear,  // Add year at the top level
          tenantName: selectedTenant?.fullName,
          tenantId: tenantId,
          apartmentNumber: apartmentNumber,
          buildingNumber: buildingNumber,
          paymentMethod: paymentMethod,
          paymentType: paymentType, // Add payment type for WhatsApp message
          additionalDetails: additionalPaymentDetails,
          checkNumber: checkNumber,
          email: email,
          totalAmount: totalAmount,
          monthlyAmount: monthlyAmount
        });

        // Show success screen after animation completes
        setTimeout(() => {
          setIsPaymentComplete(true);
        }, 2100);

      } else if (successfulPayments.length > 0 && failedPayments.length > 0) {
        // Partial success
        const successMonthNames = successfulPayments.map(p =>
          months.find(m => m.value === String(p.month).padStart(2, '0'))?.label
        ).join(', ');

        const failedMonthNames = failedPayments.map(p =>
          months.find(m => m.value === String(p.month).padStart(2, '0'))?.label
        ).join(', ');

        alert(`תשלומים הושלמו חלקית:\n\n` +
          `הצליחו (${successfulPayments.length}): ${successMonthNames}\n\n` +
          `נכשלו (${failedPayments.length}): ${failedMonthNames}\n\n` +
          `סכום שחויב: ₪${(monthlyAmount * successfulPayments.length).toLocaleString()}\n\n` +
          `אנא נסה שוב עבור החודשים שנכשלו.`);

        // Refresh the payments list to show successful ones
        if (currentView === "list") {
          await fetchPayments();
        }

      } else {
        // All payments failed
        const errorMessages = failedPayments.map(p =>
          `${months.find(m => m.value === String(p.month).padStart(2, '0'))?.label}: ${p.error}`
        ).join('\n');

        alert(`כל התשלומים נכשלו:\n\n${errorMessages}`);
      }

    } catch (error) {
      setIsProcessing(false);
      alert(`שגיאה כללית בביצוע התשלומים: ${error.message}`);
    }
  };

  // Receipt Modal Component
  const ReceiptModal = () => {
    if (!receiptModalVisible || !selectedGroupForReceipt) return null;

    const group = selectedGroupForReceipt;

    return (
      <div className={styles.modalOverlay} onClick={handleCloseReceiptModal}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              <Receipt className={styles.modalTitleIcon} />
              קבלות תשלום - דירה {group.apartmentNumber}
            </h3>
            <button
              onClick={handleCloseReceiptModal}
              className={styles.modalCloseButton}
            >
              <X />
            </button>
          </div>

          <div className={styles.modalContent}>
            {isLoadingReceipts && (
              <div className={styles.loadingContainer}>
                <Loader className={styles.loadingIcon} />
                <span>טוען קבלות...</span>
              </div>
            )}

            {receiptsError && (
              <div className={styles.errorContainer}>
                <span className={styles.errorText}>{receiptsError}</span>
              </div>
            )}

            {!isLoadingReceipts && !receiptsError && (
              <>
                <div className={styles.receiptSummary}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>דירה:</span>
                    <span className={styles.summaryValue}>{group.apartmentNumber}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>שנה:</span>
                    <span className={styles.summaryValue}>{group.year}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>דייר:</span>
                    <span className={styles.summaryValue}>{group.tenantName || group.tenantEmail}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>סה״כ קבלות:</span>
                    <span className={styles.summaryValue}>{receipts.length}</span>
                  </div>
                </div>

                {receipts.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Receipt className={styles.emptyStateIcon} />
                    <p>לא נמצאו קבלות עבור קבוצת תשלומים זו</p>
                  </div>
                ) : (
                  <div className={styles.receiptsGrid}>
                    {receipts.map((receipt, index) => (
                      <div key={`receipt-${receipt.paymentId}-${index}`} className={styles.receiptCard}>
                        <div className={styles.receiptCardHeader}>
                          <div className={styles.receiptCardTitle}>
                            <Receipt className={styles.receiptCardIcon} />
                            <span>קבלה #{receipt.paymentId}</span>
                          </div>
                          <div className={styles.receiptCardAmount}>
                            ₪{receipt.amount?.toLocaleString('he-IL') || '0'}
                          </div>
                        </div>

                        <div className={styles.receiptCardBody}>
                          <div className={styles.receiptCardInfo}>
                            <Calendar size={14} />
                            <span>{new Date(receipt.date).toLocaleDateString('he-IL')}</span>
                          </div>
                          <div className={styles.receiptCardInfo}>
                            <FileText size={14} />
                            <span>{receipt.paymentType}</span>
                          </div>
                          {receipt.fileName && (
                            <div className={styles.receiptCardInfo}>
                              <FolderOpen size={14} />
                              <span className={styles.fileName}>{receipt.fileName}</span>
                            </div>
                          )}
                        </div>

                        <div className={styles.receiptCardActions}>
                          <button
                            className={styles.receiptActionButton}
                            onClick={async () => {
                              try {
                                await viewPaymentReceiptInNewTab(receipt.paymentId);
                              } catch (error) {
                                alert('שגיאה בצפייה בקבלה');
                              }
                            }}
                            title="צפה בקבלה"
                          >
                            <ExternalLink size={14} />
                            צפה
                          </button>
                          <button
                            className={styles.receiptActionButton}
                            onClick={async () => {
                              try {
                                await downloadPaymentReceipt(receipt.paymentId);
                              } catch (error) {
                                alert('שגיאה בהורדת הקבלה');
                              }
                            }}
                            title="הורד קבלה"
                          >
                            <Download size={14} />
                            הורד
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Email Files Modal Component
  const EmailFilesModal = () => {
    if (!emailModalVisible || !selectedGroupForEmail) return null;

    const group = selectedGroupForEmail;

    return (
      <div className={styles.modalOverlay} onClick={handleCloseEmailModal}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              <Mail className={styles.modalTitleIcon} />
              קבצי אימייל - דירה {group.apartmentNumber}
            </h3>
            <button
              onClick={handleCloseEmailModal}
              className={styles.modalCloseButton}
            >
              <X />
            </button>
          </div>

          <div className={styles.modalContent}>
            {isLoadingEmailFiles && (
              <div className={styles.loadingContainer}>
                <Loader className={styles.loadingIcon} />
                <span>טוען קבצי אימייל...</span>
              </div>
            )}

            {emailFilesError && (
              <div className={styles.errorContainer}>
                <span className={styles.errorText}>{emailFilesError}</span>
              </div>
            )}

            {!isLoadingEmailFiles && !emailFilesError && (
              <>
                <div className={styles.receiptSummary}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>דירה:</span>
                    <span className={styles.summaryValue}>{group.apartmentNumber}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>שנה:</span>
                    <span className={styles.summaryValue}>{group.year}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>דייר:</span>
                    <span className={styles.summaryValue}>{group.tenantName || group.tenantEmail}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>סה״כ קבצי אימייל:</span>
                    <span className={styles.summaryValue}>{emailFiles.length}</span>
                  </div>
                </div>

                {emailFiles.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Mail className={styles.emptyStateIcon} />
                    <p>לא נמצאו קבצי אימייל עבור קבוצת תשלומים זו</p>
                  </div>
                ) : (
                  <div className={styles.receiptsGrid}>
                    {emailFiles.map((emailFile, index) => (
                      <div key={`email-${emailFile.paymentId}-${index}`} className={styles.receiptCard}>
                        <div className={styles.receiptCardHeader}>
                          <div className={styles.receiptCardTitle}>
                            <Mail className={styles.receiptCardIcon} />
                            <span>אימייל #{emailFile.paymentId}</span>
                          </div>
                          <div className={styles.receiptCardAmount}>
                            ₪{emailFile.amount?.toLocaleString('he-IL') || '0'}
                          </div>
                        </div>

                        <div className={styles.receiptCardBody}>
                          <div className={styles.receiptCardInfo}>
                            <Calendar size={14} />
                            <span>{new Date(emailFile.date).toLocaleDateString('he-IL')}</span>
                          </div>
                          <div className={styles.receiptCardInfo}>
                            <FileText size={14} />
                            <span>{emailFile.paymentType}</span>
                          </div>
                          {emailFile.referenceNumber && (
                            <div className={styles.receiptCardInfo}>
                              <Receipt size={14} />
                              <span>{emailFile.referenceNumber}</span>
                            </div>
                          )}
                          {emailFile.fileName && (
                            <div className={styles.receiptCardInfo}>
                              <FolderOpen size={14} />
                              <span className={styles.fileName}>{emailFile.fileName}</span>
                            </div>
                          )}
                          {emailFile.generatedAt && (
                            <div className={styles.receiptCardInfo}>
                              <Clock size={14} />
                              <span>{new Date(emailFile.generatedAt).toLocaleDateString('he-IL')}</span>
                            </div>
                          )}
                        </div>

                        <div className={styles.receiptCardActions}>
                          <button
                            className={styles.receiptActionButton}
                            onClick={async () => {
                              try {
                                await viewEmailContentInNewTab(emailFile.filePath);
                              } catch (error) {
                                alert('שגיאה בצפייה בקובץ האימייל');
                              }
                            }}
                            title="צפה בקובץ אימייל"
                          >
                            <ExternalLink size={14} />
                            צפה
                          </button>
                          <button
                            className={styles.receiptActionButton}
                            onClick={async () => {
                              try {
                                await downloadEmailContentFile(emailFile.filePath, emailFile.fileName);
                              } catch (error) {
                                alert('שגיאה בהורדת קובץ האימייל');
                              }
                            }}
                            title="הורד קובץ אימייל"
                          >
                            <Download size={14} />
                            הורד
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Month toggle functions
  const handleMonthToggle = (monthValue) => {
    // Only handle unpaid months - paid months are disabled and only trash icon works
    if (alreadyPaidMonths.includes(monthValue)) {
      return; // Do nothing for paid months
    }

    setSelectedMonths(prev => {
      if (prev.includes(monthValue)) {
        return prev.filter(month => month !== monthValue);
      } else {
        return [...prev, monthValue].sort();
      }
    });
  };

  const handleSelectAllMonths = () => {
    const unpaidMonths = months
      .map(month => month.value)
      .filter(monthValue => !alreadyPaidMonths.includes(monthValue));

    const newlySelectedMonths = getNewlySelectedMonths();

    if (newlySelectedMonths.length === unpaidMonths.length) {
      setSelectedMonths(selectedMonths.filter(month => alreadyPaidMonths.includes(month)));
    } else {
      setSelectedMonths([...alreadyPaidMonths, ...unpaidMonths]);
    }
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);

    // If "one_time" (תשלום חד פעמי) is selected, uncheck all months and only select current month
    if (type === "one_time") {
      const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
      setSelectedMonths([currentMonth]);
    }
  };

  const getSelectedMonthNames = () => {
    return selectedMonths
      .map(value => months.find(month => month.value === value)?.label)
      .filter(Boolean)
      .join(', ');
  };

  // Direct delete function with confirmation
  const handleDeleteMonthPaymentDirect = async (monthValue, monthLabel) => {
    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך למחוק את התשלום עבור ${monthLabel}?\n` +
      `פעולה זו תמחק את התשלום מהמסד הנתונים ולא ניתן לבטלה.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      // Find the payment for this specific month from grouped payments
      const payment = getPaymentForMonth(monthValue);
      
      if (!payment) {
        await deletePaymentByApartmentMonthYear(monthValue, monthLabel);
        return;
      }

      const paymentId = payment.id || payment.paymentId || payment.PaymentId || payment.paymentID;

      if (!paymentId) {
        await deletePaymentByApartmentMonthYear(monthValue, monthLabel);
        return;
      }

      // Use the same authenticated fetch method as other API calls
      const { authenticatedFetchWithRefresh } = await import('../../utils/enhancedTokenManager');
      
      const response = await authenticatedFetchWithRefresh(`${process.env.REACT_APP_API_URL}/api/Payment/DeleteVaadPayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ PaymentId: paymentId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`התשלום עבור ${monthLabel} נמחק בהצלחה`);
        
        // Refresh payments data
        await fetchPayments();
        
        // Update UI state
        setAlreadyPaidMonths(prev => prev.filter(month => month !== monthValue));
        setSelectedMonths(prev => prev.filter(month => month !== monthValue));
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'שגיאה במחיקת התשלום');
      }
      
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert(`שגיאה במחיקת התשלום עבור ${monthLabel}: ${error.message}`);
    }
  };

  // Alternative deletion method if payment ID is not found
  const deletePaymentByApartmentMonthYear = async (monthValue, monthLabel) => {
    try {
      // Try to find payment ID from the raw payments array
      let foundPaymentId = null;
      
      // Search through all payments to find one matching apartment, month, year
      const monthNumber = parseInt(monthValue);
      const targetYear = parseInt(paymentYear);
      
      for (const payment of payments) {
        const paymentDate = new Date(payment.date || payment.Date);
        const paymentMonth = paymentDate.getMonth() + 1;
        const paymentYear = paymentDate.getFullYear();
        const paymentApartment = payment.apartmentNumber || payment.ApartmentNumber;
        
        if (paymentApartment?.toString() === apartmentNumber?.toString() &&
            paymentMonth === monthNumber &&
            paymentYear === targetYear) {
          foundPaymentId = payment.paymentID || payment.PaymentID || payment.id;
          break;
        }
      }
      
      if (foundPaymentId) {
        // Use the same authenticated fetch method as other API calls
        const { authenticatedFetchWithRefresh } = await import('../../utils/enhancedTokenManager');
        
        const response = await authenticatedFetchWithRefresh(`${process.env.REACT_APP_API_URL}/api/Payment/DeleteVaadPayment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ PaymentId: foundPaymentId })
        });

        if (response.ok) {
          const result = await response.json();
          alert(`התשלום עבור ${monthLabel} נמחק בהצלחה`);
          
          // Refresh payments data
          await fetchPayments();
          
          // Update UI state
          setAlreadyPaidMonths(prev => prev.filter(month => month !== monthValue));
          setSelectedMonths(prev => prev.filter(month => month !== monthValue));
          
          return;
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'שגיאה במחיקת התשלום');
        }
      }
      
      // If still no payment found, show error
      alert(`לא ניתן למצוא את נתוני התשלום עבור ${monthLabel}. ` +
            `אנא נסה לרענן את הדף ולנסות שוב, או פנה למנהל המערכת.`);

    } catch (error) {
      alert(`שגיאה במחיקת התשלום עבור ${monthLabel}: ${error.message}`);
    }
  };

  // Handle editing a specific month's payment
  const handleEditMonthPayment = async (monthValue) => {
    try {
      // Find the payment for this specific month
      const payment = getPaymentForMonth(monthValue);
      
      if (!payment) {
        alert('לא נמצא תשלום עבור חודש זה');
        return;
      }

      // Set the form with this payment's data
      setEditingPaymentId(payment.id || payment.paymentId || payment.PaymentId);
      setOriginalPaymentData(payment);
      
      // Set form fields
      setSelectedMonths([monthValue]); // Set the specific month being edited
      setPaymentYear(payment.paymentYear || paymentYear);
      setAmount(payment.amount || payment.Amount || 0);
      
      // Switch to edit view
      setCurrentView("edit");
      
    } catch (error) {
      console.error('Error editing payment:', error);
      alert('שגיאה בעריכת התשלום');
    }
  };

  // Handle deleting a specific month's payment
  const handleDeleteMonthPayment = async (monthValue) => {
    try {
      const payment = getPaymentForMonth(monthValue);
      
      if (!payment) {
        alert('לא נמצא תשלום למחיקה');
        return;
      }

      const paymentId = payment.id || payment.paymentId || payment.PaymentId || payment.paymentID;
      
      if (!paymentId) {
        alert('לא נמצא מזהה תשלום למחיקה');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/DeleteVaadPayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ PaymentId: paymentId })
      });

      if (response.ok) {
        const result = await response.json();
        alert('התשלום נמחק בהצלחה');

        await fetchPayments();

        setAlreadyPaidMonths(prev => prev.filter(month => month !== monthValue));
        setSelectedMonths(prev => prev.filter(month => month !== monthValue));

      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'שגיאה במחיקת התשלום');
      }

    } catch (error) {
      alert(`שגיאה במחיקת התשלום: ${error.message}`);
    }
  };

  // Helper function to find payment for specific month
  const getPaymentForMonth = (monthValue) => {
    const matchingGroup = groupedPayments.find(group => {
      const apartmentMatch = group.apartmentNumber && apartmentNumber &&
        group.apartmentNumber.toString() === apartmentNumber.toString();
      const yearMatch = group.year && paymentYear &&
        group.year.toString() === paymentYear.toString();

      if (!apartmentMatch || !yearMatch) return false;
      return group.months && group.months.includes(monthValue);
    });

    if (!matchingGroup) return null;
    if (!matchingGroup.paymentDetails || matchingGroup.paymentDetails.length === 0) return null;

    const monthNumber = parseInt(monthValue);
    const payment = matchingGroup.paymentDetails.find(payment => {
      const paymentDate = new Date(payment.date || payment.Date);
      const paymentMonth = paymentDate.getMonth() + 1;
      return paymentMonth === monthNumber;
    });

    return payment;
  };

  // Event listeners
  useEffect(() => {
    const handleBuildingChange = () => {
      if (currentView === "list") {
        fetchPayments();
      }
      if (currentView === "form" || currentView === "edit") {
        fetchUsers();
        fetchVaadMonthlyData();
      }
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('userDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('userDataChanged', handleBuildingChange);
    };
  }, [currentView]);

  useEffect(() => {
    if (currentView === "list") {
      fetchPayments();
    }
    if (currentView === "form" || currentView === "edit") {
      fetchUsers();
      fetchVaadMonthlyData();
    }
  }, [currentView]);

  // Initialize on component mount (handles page refresh)
  useEffect(() => {
    // Always fetch payments on mount to populate groupedPayments
    fetchPayments();

    // If we're on the form view, also fetch users and vaad data
    if (currentView === "form" || currentView === "edit") {
      fetchUsers();
      fetchVaadMonthlyData();
    }
  }, []); // Empty dependency array - runs only on mount

  // Fetch users when "דיירים שלא שילמו תשלום חד פעמי" filter is activated
  useEffect(() => {
    if (showUnpaidOneTimeOnly && users.length === 0 && !isLoadingUsers) {
      fetchUsers();
    }
  }, [showUnpaidOneTimeOnly]);

  // Fetch VaadMonthly data
  const fetchVaadMonthlyData = async () => {
    try {
      setIsLoadingVaadMonthly(true);
      setVaadMonthlyError("");

      const userData = JSON.parse(localStorage.getItem('user'));
      const currentBuildingId = userData?.buildingId;

      if (!currentBuildingId) {
        setVaadMonthlyError("לא נמצא מזהה בניין");
        return;
      }

      const response = await getListVaadMonthlyByBuildingId(currentBuildingId);


      const vaadMonthlyMap = {};

      if (response && response.userData) {

        response.userData.forEach(userDataItem => {

          const possibleUserIds = [
            userDataItem.userID,
            userDataItem.userId,
            userDataItem.UserID,
            userDataItem.userid,
            userDataItem.id,
            userDataItem.Id,
            userDataItem.ID
          ];

          const userId = possibleUserIds.find(id => id !== undefined && id !== null);
          const vaadAmount = userDataItem.vaadMonthly || userDataItem.VaadMonthly || 0;
          const email = userDataItem.email || userDataItem.Email;


          if (userId) {
            vaadMonthlyMap[userId] = vaadAmount;
          }

          if (email) {
            vaadMonthlyMap[email.toLowerCase()] = vaadAmount;
          }

          const fullName = userDataItem.fullName || userDataItem.FullName;
          if (fullName) {
            vaadMonthlyMap[fullName.toLowerCase()] = vaadAmount;
          }
        });
      }

      setVaadMonthlyData(vaadMonthlyMap);
    } catch (error) {
      setVaadMonthlyError("שגיאה בטעינת נתוני תשלום חודשי");
      setVaadMonthlyData({});
    } finally {
      setIsLoadingVaadMonthly(false);
    }
  };

  // Auto-update email when tenant is selected
  useEffect(() => {
    if (selectedTenant && selectedTenant.email && (currentView === "form" || currentView === "edit")) {
      setEmail(selectedTenant.email);
    }
  }, [selectedTenant, currentView]);

  // Handle tenant selection after users are loaded during edit
  useEffect(() => {
    if (currentView === "edit" && users.length > 0 && originalPaymentData && !selectedTenant) {
      findAndSelectTenant(originalPaymentData);
    }
  }, [users, currentView, originalPaymentData, selectedTenant]);
  

  // Auto-focus search input when tenant dropdown opens
  useEffect(() => {
    if (isTenantDropdownOpen && tenantSearchInputRef.current) {
      setTimeout(() => {
        tenantSearchInputRef.current?.focus();
      }, 100);
    }
  }, [isTenantDropdownOpen]);

  // Update grouped payments when payments change
  useEffect(() => {
    if (payments.length > 0) {
      const grouped = groupPaymentsByApartmentAndYear(payments);
      setGroupedPayments(grouped);
    } else {
      setGroupedPayments([]);
    }
  }, [payments]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isExportDropdownOpen && exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setIsExportDropdownOpen(false);
      }
      if (isMobileDropdownOpen && mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setIsMobileDropdownOpen(false);
      }
      if (isWhatsappOneTimeDropdownOpen && whatsappOneTimeDropdownRef.current && !whatsappOneTimeDropdownRef.current.contains(event.target)) {
        setIsWhatsappOneTimeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportDropdownOpen, isMobileDropdownOpen, isWhatsappOneTimeDropdownOpen]);

  // Fetch payments from backend
  const fetchPayments = async () => {
    try {
      setIsLoadingPayments(true);
      setPaymentsError("");

      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;
      setBuildingId(buildingId);
      if (!buildingId) {
        setPaymentsError("לא נמצא מזהה בניין");
        return;
      }

      const response = await getListPaymentsByBuildingId(buildingId);

      setPayments(response || []);
    } catch (error) {
      setPaymentsError("שגיאה בטעינת רשימת התשלומים");
      setPayments([]);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      setUsersError("");

      const userData = JSON.parse(localStorage.getItem('user'));
      const currentBuildingId = userData?.buildingId;

      if (!currentBuildingId) {
        setUsersError("לא נמצא מזהה בניין");
        return;
      }

      const response = await getListUsers(currentBuildingId);

      // Filter to show only tenants (IsTenant = "כן")
      const tenants = response.filter(user =>
        user.IsTenant === "כן" || user.isTenant === "כן" ||
        user.IsTenant === true || user.isTenant === true
      );
      setUsers(tenants);
    } catch (error) {
      setUsersError("שגיאה בטעינת רשימת הדיירים");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleViewDetails = (groupedPayment) => {
    setSelectedPaymentForView(groupedPayment);
    setViewModalVisible(true);
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedPaymentForView(null);
  };

  // Get one-time payment amount from existing payments
  const getOneTimePaymentAmount = () => {
    // Find an existing one-time payment to get the amount
    for (const group of groupedPayments) {
      const oneTimePayment = group.paymentDetails?.find(payment =>
        payment.PaymentType === "חד פעמי" || payment.paymentType === "חד פעמי"
      );
      if (oneTimePayment) {
        return oneTimePayment.amount || oneTimePayment.Amount || 0;
      }
    }
    return 0;
  };

  // Send WhatsApp message with list of users who haven't paid one-time payment
  const sendWhatsAppUnpaidOneTimeList = (includePhones = false) => {
    if (usersWithoutOneTimePayment.length === 0) {
      alert('אין דיירים שלא שילמו תשלום חד פעמי');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('user'));
    const buildingAddress = userData?.buildingAddress || 'הבניין';
    const oneTimeAmount = getOneTimePaymentAmount();
    const totalDebt = oneTimeAmount * usersWithoutOneTimePayment.length;

    let message = `🏠 *דוח דיירים שלא שילמו תשלום חד פעמי*\n`;
    message += `📍 ${buildingAddress}\n`;
    message += `📅 תאריך: ${new Date().toLocaleDateString('he-IL')}\n`;
    message += `${'═'.repeat(30)}\n\n`;

    message += `📊 *סיכום:*\n`;
    message += `• מספר דיירים: ${usersWithoutOneTimePayment.length}\n`;
    if (oneTimeAmount > 0) {
      message += `• סכום לתשלום: ₪${oneTimeAmount.toLocaleString()}\n`;
      message += `• סה"כ חוב: ₪${totalDebt.toLocaleString()}\n`;
    }
    message += `\n`;

    message += `📋 *רשימת דיירים:*\n\n`;

    // Sort users by apartment number
    const sortedUsers = [...usersWithoutOneTimePayment].sort((a, b) => {
      const numA = parseInt(a.ApartmentNumber || a.apartmentNumber) || 0;
      const numB = parseInt(b.ApartmentNumber || b.apartmentNumber) || 0;
      return numA - numB;
    });

    sortedUsers.forEach((user, index) => {
      const userName = user.FullName || user.fullName || `${user.FirstName || user.firstName || ''} ${user.LastName || user.lastName || ''}`.trim() || '-';
      const aptNumber = user.ApartmentNumber || user.apartmentNumber || '-';
      const phone = user.Phone || user.phone || '';

      message += `${index + 1}. *דירה ${aptNumber}*\n`;
      message += `   👤 ${userName}\n`;
      if (oneTimeAmount > 0) {
        message += `   💰 ₪${oneTimeAmount.toLocaleString()}\n`;
      }

      if (includePhones) {
        if (phone) {
          message += `   📞 ${phone}\n`;
        } else {
          message += `   ❌ אין מספר טלפון\n`;
        }
      }
      message += `\n`;
    });

    message += `${'─'.repeat(30)}\n`;
    message += `💻 *נוצר ע"י bildiapp.com*\n`;
    message += `⏰ ${new Date().toLocaleString('he-IL')}`;

    // Send WhatsApp message (no phone number = user chooses contact)
    const whatsappTab = sendWhatsAppMessage(message, null);

    if (!whatsappTab) {
      alert('❌ חלון WhatsApp נחסם על ידי הדפדפן. אנא אשר חלונות קופצים.');
    }
  };

  // Print unpaid one-time payment users list
  const printUnpaidOneTimeList = () => {
    if (usersWithoutOneTimePayment.length === 0) {
      alert('אין דיירים שלא שילמו תשלום חד פעמי');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('user'));
    const buildingAddress = userData?.buildingAddress || 'הבניין';
    const oneTimeAmount = getOneTimePaymentAmount();
    const totalDebt = oneTimeAmount * usersWithoutOneTimePayment.length;

    // Sort users by apartment number
    const sortedUsers = [...usersWithoutOneTimePayment].sort((a, b) => {
      const numA = parseInt(a.ApartmentNumber || a.apartmentNumber) || 0;
      const numB = parseInt(b.ApartmentNumber || b.apartmentNumber) || 0;
      return numA - numB;
    });

    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8">
        <title>דיירים שלא שילמו תשלום חד פעמי</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
            padding: 20px;
            direction: rtl;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 2px solid #dc3545;
          }
          .header h1 {
            color: #dc3545;
            font-size: 24px;
            margin-bottom: 8px;
          }
          .header .building {
            font-size: 16px;
            color: #666;
            margin-bottom: 5px;
          }
          .header .date {
            font-size: 14px;
            color: #888;
          }
          .summary {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-around;
            flex-wrap: wrap;
            gap: 10px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-item .label {
            font-size: 12px;
            color: #666;
          }
          .summary-item .value {
            font-size: 18px;
            font-weight: bold;
            color: #dc3545;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
          }
          th {
            background: #dc3545;
            color: white;
            padding: 12px 8px;
            text-align: right;
            font-weight: 600;
          }
          td {
            padding: 10px 8px;
            border-bottom: 1px solid #eee;
            text-align: right;
          }
          tr:nth-child(even) { background: #f9f9f9; }
          tr:hover { background: #fff3f3; }
          .row-number {
            width: 40px;
            text-align: center;
            color: #888;
            font-size: 12px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #888;
            padding-top: 15px;
            border-top: 1px solid #eee;
          }
          @media print {
            body { padding: 10px; }
            .header { margin-bottom: 15px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏠 דיירים שלא שילמו תשלום חד פעמי</h1>
          <div class="building">${buildingAddress}</div>
          <div class="date">תאריך: ${new Date().toLocaleDateString('he-IL')}</div>
        </div>

        <div class="summary">
          <div class="summary-item">
            <div class="label">מספר דיירים</div>
            <div class="value">${usersWithoutOneTimePayment.length}</div>
          </div>
          ${oneTimeAmount > 0 ? `
          <div class="summary-item">
            <div class="label">סכום לתשלום</div>
            <div class="value">₪${oneTimeAmount.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="label">סה"כ חוב</div>
            <div class="value">₪${totalDebt.toLocaleString()}</div>
          </div>
          ` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th class="row-number">#</th>
              <th>שם דייר</th>
              <th>דירה</th>
              <th>טלפון</th>
              <th>אימייל</th>
              ${oneTimeAmount > 0 ? '<th>סכום</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${sortedUsers.map((user, index) => {
              const userName = user.FullName || user.fullName || `${user.FirstName || user.firstName || ''} ${user.LastName || user.lastName || ''}`.trim() || '-';
              const aptNumber = user.ApartmentNumber || user.apartmentNumber || '-';
              const phone = user.Phone || user.phone || '-';
              const email = user.Email || user.email || '-';
              return `
                <tr>
                  <td class="row-number">${index + 1}</td>
                  <td>${userName}</td>
                  <td>${aptNumber}</td>
                  <td>${phone}</td>
                  <td>${email}</td>
                  ${oneTimeAmount > 0 ? `<td>₪${oneTimeAmount.toLocaleString()}</td>` : ''}
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          נוצר ע"י bildiapp.com | ${new Date().toLocaleString('he-IL')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      alert('❌ חלון ההדפסה נחסם על ידי הדפדפן. אנא אשר חלונות קופצים.');
    }
  };

  // Updated handleEditPayment to handle new payment methods
  const handleEditPayment = async (groupedPayment) => {
    const latestPayment = groupedPayment.paymentDetails
      .sort((a, b) => new Date(b.date || b.Date) - new Date(a.date || a.Date))[0];


    const PaymentId = latestPayment.id || latestPayment.paymentId || latestPayment.Id || latestPayment.PaymentId || latestPayment.paymentID;

    if (!PaymentId) {
      alert('שגיאה: לא נמצא מזהה תשלום');
      return;
    }

    setEditingPaymentId(PaymentId);
    // Store both latestPayment and groupedPayment info for later use
    setOriginalPaymentData({
      ...latestPayment,
      groupTenantEmail: groupedPayment.tenantEmail || groupedPayment.TenantEmail,
      groupTenantEmails: groupedPayment.tenantEmails
    });
    setSelectedTenant(null);

    setTenantId("");
    setApartmentNumber(latestPayment.apartmentNumber || latestPayment.ApartmentNumber || groupedPayment.apartmentNumber || "");
    setBuildingNumber(latestPayment.buildingNumber || latestPayment.BuildingNumber || groupedPayment.buildingNumber || "");
    setAmount((latestPayment.amount || latestPayment.Amount)?.toString() || "");

    // Get email from payment, group, or tenantEmails array
    const paymentEmail = latestPayment.tenantEmail || latestPayment.TenantEmail || latestPayment.userEmail || latestPayment.UserEmail;
    const groupEmail = groupedPayment.tenantEmail || groupedPayment.TenantEmail;
    const firstTenantEmail = groupedPayment.tenantEmails?.length > 0 ? groupedPayment.tenantEmails[0] : null;
    setEmail(paymentEmail || groupEmail || firstTenantEmail || "");

    setAdditionalPaymentDetails(latestPayment.additionalDetails || latestPayment.AdditionalDetails || "");
    setCheckNumber(latestPayment.checkNumber || latestPayment.CheckNumber || "");

    const paymentTypeText = latestPayment.paymentType || latestPayment.PaymentType;
    // Set payment type based on the payment's PaymentType field
    if (paymentTypeText === "חד פעמי") {
      setPaymentType("one_time");
    } else {
      setPaymentType("regular");
    }

    // Updated payment method mapping
    const method = latestPayment.paymentMethod || latestPayment.PaymentMethod;
    let methodId = "cash"; // default
    if (method === "צ'ק") {
      methodId = "check";
    } else if (method === "העברה בנקאית") {
      methodId = "bank_transfer";
    } else if (method === "מזומן") {
      methodId = "cash";
    }
    setPaymentMethod(methodId);

    const paymentDate = latestPayment.date || latestPayment.Date;
    if (paymentDate) {
      const date = new Date(paymentDate);
      const allMonthsInGroup = groupedPayment.months.map(month =>
        String(month).padStart(2, '0')
      );
      setSelectedMonths(allMonthsInGroup);
      // Set alreadyPaidMonths so we can detect newly selected months when updating
      setAlreadyPaidMonths(allMonthsInGroup);
      setPaymentYear(date.getFullYear().toString());
    }

    setCurrentView("edit");
  };

  const findAndSelectTenant = (payment) => {

    const paymentTenantEmail = payment.tenantEmail || payment.TenantEmail || payment.userEmail || payment.UserEmail;
    const paymentApartmentNumber = payment.apartmentNumber || payment.ApartmentNumber;
    const paymentTenantName = payment.tenantName || payment.TenantName;

    const matchingTenant = users.find(user => {
      const emailMatch = user.email && paymentTenantEmail &&
        user.email.toLowerCase() === paymentTenantEmail.toLowerCase();
      const apartmentMatch = user.apartmentNumber && paymentApartmentNumber &&
        user.apartmentNumber.toString() === paymentApartmentNumber.toString();
      const nameMatch = user.fullName && paymentTenantName &&
        user.fullName.toLowerCase() === paymentTenantName.toLowerCase();

      return emailMatch || (apartmentMatch && nameMatch) || nameMatch;
    });


    if (matchingTenant) {
      setSelectedTenant(matchingTenant);

      if (vaadMonthlyData[matchingTenant.userId] && !amount) {
        const vaadAmount = vaadMonthlyData[matchingTenant.userId];
        setAmount(vaadAmount.toString());
      }

    } else {
      setEmail(paymentTenantEmail || "");
    }
  };

  const handleCancelEdit = () => {
    resetFormFields();
    setEditingPaymentId(null);
    setOriginalPaymentData(null);
    setCurrentView("list");
  };

  // UPDATED: handleUpdatePayment for single month updates
  const handleUpdatePayment = async () => {
    setIsProcessing(true);

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;

      // Get email from multiple sources - current email, original payment, or group (optional)
      const tenantEmail = email ||
        originalPaymentData?.tenantEmail ||
        originalPaymentData?.TenantEmail ||
        originalPaymentData?.userEmail ||
        originalPaymentData?.UserEmail ||
        originalPaymentData?.groupTenantEmail ||
        (originalPaymentData?.groupTenantEmails?.length > 0 ? originalPaymentData.groupTenantEmails[0] : null) ||
        "";

      // Get payment type text based on paymentType state
      const paymentTypeText = paymentType === "one_time" ? "חד פעמי" : "ועד בית";

      // Get the month from the original payment being edited
      const originalMonth = originalPaymentData?.paymentMonth || originalPaymentData?.PaymentMonth;
      const selectedMonth = originalMonth ? parseInt(originalMonth) : (selectedMonths.length > 0 ? parseInt(selectedMonths[0]) : 1);

      const updatedPaymentData = {
        PaymentId: editingPaymentId,
        BuildingId: buildingId,
        ApartmentNumber: apartmentNumber,
        BuildingNumber: buildingNumber,
        Date: new Date(`${paymentYear}-${String(selectedMonth).padStart(2, '0')}-01T12:00:00`).toISOString(),
        Amount: parseFloat(amount),
        PaymentMethod: getPaymentMethodName(paymentMethod),
        PaymentType: paymentTypeText,
        AdditionalDetails: additionalPaymentDetails,
        CheckNumber: paymentMethod === "check" ? checkNumber : null,
        TenantEmail: tenantEmail,
        CardLastFour: null,
        PaymentMonth: selectedMonth,
        PaymentYear: parseInt(paymentYear)
      };

      const response = await UpdatePaymentVaad(updatedPaymentData);

      if (response.success) {
        await fetchPayments();
        setIsProcessing(false);

        setTimeout(() => {
          const emailSavedMessage = response.emailSaved ? 'הקבלה עודכנה ונשמרה במערכת' : 'הקבלה עודכנה';
          alert(`התשלום עודכן בהצלחה!\n${emailSavedMessage}`);
          resetFormFields();
          setEditingPaymentId(null);
          setOriginalPaymentData(null);
          setCurrentView("list");
        }, 1000);
      } else {
        throw new Error(response.message || 'Update failed');
      }

    } catch (error) {
      setIsProcessing(false);

      let errorMessage = 'Unknown error occurred';
      if (error.message) {
        try {
          const errorObj = JSON.parse(error.message);
          errorMessage = errorObj.message || errorObj.title || error.message;
        } catch {
          errorMessage = error.message;
        }
      }

      alert(`שגיאה בעדכון התשלום: ${errorMessage}`);
    }
  };

  // Updated resetFormFields - removed credit card fields
  const resetFormFields = () => {
    setSelectedTenant(null);
    setTenantId("");
    setApartmentNumber("");
    setBuildingNumber("");
    setAmount("");
    setEmail("");
    setAdditionalPaymentDetails("");
    setSelectedMonths([]);
    setAlreadyPaidMonths([]);
    setPaymentYear(new Date().getFullYear().toString());
    setPaymentType("regular"); // Default to תשלום ועד בית
    setPaymentMethod("cash");
    setCheckNumber("");
    setVaadMonthlyError("");
  };

  // Handler for creating a new payment
  const handleNewPayment = () => {
    resetFormFields();
    setCurrentView("form");
  };

  // Filter grouped payments based on search term and apartment filter
  const filteredGroupedPayments = groupedPayments
    .map(group => {
      // Filter payments based on payment type
      // By default: show only "ועד בית" payments (exclude "חד פעמי")
      // When checkbox is checked: show only "חד פעמי" payments
      const filteredPayments = group.paymentDetails?.filter(payment => {
        const paymentType = payment.paymentType || payment.PaymentType || payment.type || '';
        // Check for one-time payment - handles "חד פעמי", "תשלום חד פעמי", etc.
        const isOneTimePayment = paymentType.includes("חד פעמי");

        if (showOneTimePaymentsOnly) {
          return isOneTimePayment; // Show only one-time payments
        } else {
          return !isOneTimePayment; // Show only regular payments (ועד בית)
        }
      }) || [];

      // If no payments match the filter, return null to filter it out later
      if (filteredPayments.length === 0) {
        return null;
      }

      // Recalculate group totals based on filtered payments
      const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || p.Amount || 0), 0);
      const months = new Set(filteredPayments.map(p => new Date(p.date || p.Date).getMonth() + 1));
      const paymentMethods = new Set(filteredPayments.map(p => p.paymentMethod || p.PaymentMethod));

      return {
        ...group,
        paymentDetails: filteredPayments,
        totalAmount,
        months: Array.from(months).sort((a, b) => a - b),
        paymentMethods: Array.from(paymentMethods)
      };
    })
    .filter(group => {
      if (!group) return false;

      // Unpaid filter - show only apartments that haven't paid for current month in current year
      if (showUnpaidOnly) {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        // Check if this group is for current year and doesn't have current month payment
        const isCurrentYear = group.year === currentYear;
        const hasCurrentMonthPayment = group.months.includes(currentMonth);

        // Only show if it's current year and doesn't have current month payment
        if (!isCurrentYear || hasCurrentMonthPayment) {
          return false;
        }
      }

      // Search term filter
      const matchesSearch = !searchTerm || (() => {
        const searchLower = searchTerm.toLowerCase();
        return (
          group.tenantName?.toLowerCase().includes(searchLower) ||
          group.apartmentNumber?.toString().includes(searchTerm) ||
          group.totalAmount?.toString().includes(searchTerm) ||
          group.paymentMethods.some(method => method.toLowerCase().includes(searchLower)) ||
          group.year?.toString().includes(searchTerm)
        );
      })();

      // Apartment filter
      const matchesApartment = !selectedApartmentFilter ||
        group.apartmentNumber?.toString() === selectedApartmentFilter;

      return matchesSearch && matchesApartment;
    });

  // Get apartments that have paid one-time payment
  const apartmentsWithOneTimePayment = useMemo(() => {
    const apartments = new Set();
    groupedPayments.forEach(group => {
      const hasOneTimePayment = group.paymentDetails?.some(payment =>
        payment.PaymentType === "חד פעמי" || payment.paymentType === "חד פעמי"
      );
      if (hasOneTimePayment && group.apartmentNumber) {
        apartments.add(group.apartmentNumber.toString());
      }
    });
    return apartments;
  }, [groupedPayments]);

  // Get users who haven't paid one-time payment
  const usersWithoutOneTimePayment = useMemo(() => {
    if (!showUnpaidOneTimeOnly || !users.length) return [];

    return users.filter(user => {
      const userApartment = user.ApartmentNumber?.toString() || user.apartmentNumber?.toString();
      return userApartment && !apartmentsWithOneTimePayment.has(userApartment);
    });
  }, [users, apartmentsWithOneTimePayment, showUnpaidOneTimeOnly]);

  // Get unique apartment numbers for filter dropdown
  const getUniqueApartmentNumbers = () => {
    const apartmentNumbers = groupedPayments.map(group => group.apartmentNumber?.toString()).filter(Boolean);
    return [...new Set(apartmentNumbers)].sort((a, b) => parseInt(a) - parseInt(b));
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredGroupedPayments.length / itemsPerPage);
  const indexOfLastPayment = currentPage * itemsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - itemsPerPage;
  const currentGroupedPayments = filteredGroupedPayments.slice(indexOfFirstPayment, indexOfLastPayment);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit'
    });
  };

  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.buildingId || '';
  };

  const getNewlySelectedMonths = () => {
    // Ensure selectedMonths is always an array to prevent filter errors
    const monthsArray = Array.isArray(selectedMonths) ? selectedMonths : [];
    return monthsArray.filter(month => !alreadyPaidMonths.includes(month));
  };

  const getNewlySelectedMonthNames = () => {
    const newMonths = getNewlySelectedMonths();
    return newMonths
      .map(value => months.find(month => month.value === value)?.label)
      .filter(Boolean)
      .join(', ');
  };

  const formatAmount = (amount) => {
    return `₪${amount?.toLocaleString('he-IL') || '0'}`;
  };

  // String version for exports, API calls, etc.
  const formatMonthsString = (months) => {
    const monthsArray = Array.isArray(months) ? months : [];
    // Ensure months are padded with leading zeros
    return monthsArray.map(m => String(m).padStart(2, '0')).join(', ');
  };

  // JSX version for UI display in grid format
  const formatMonthsNumeric = (months, keyPrefix = 'default') => {
    // Return JSX element for grid layout (4 rows x 3 columns)
    const monthsArray = Array.isArray(months) ? months : [];

    if (monthsArray.length === 0) {
      return <span key={`empty-${keyPrefix}`}>-</span>;
    }

    // Create a unique identifier for this specific call
    const uniqueId = `${keyPrefix}-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div key={`grid-${uniqueId}`} className={styles.monthsGrid}>
        {monthsArray.map((month, index) => (
          <span key={`month-${uniqueId}-${month}-${index}`} className={styles.monthItem}>
            {month}
          </span>
        ))}
      </div>
    );
  };

  const handleTenantSelection = (tenant) => {

    setSelectedTenant(tenant);

    if (currentView !== "edit" || !apartmentNumber) {
      setApartmentNumber(tenant.apartmentNumber || "");
    }

    const paidMonths = calculateAlreadyPaidMonths(
      tenant.email,
      tenant.apartmentNumber || apartmentNumber,
      paymentYear,
      paymentType
    );
    setAlreadyPaidMonths(paidMonths);

    // Initialize selectedMonths based on payment type
    // If "one_time" payment is selected, only select current month
    // Otherwise, initialize with paid months
    if (paymentType === "one_time") {
      const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
      setSelectedMonths([currentMonth]);
    } else {
      setSelectedMonths(paidMonths);
    }

    let vaadAmount = null;

    const possibleUserIds = [
      tenant.userID,
      tenant.userId,
      tenant.UserID,
      tenant.userid,
      tenant.id,
      tenant.Id,
      tenant.ID
    ];

    for (const userId of possibleUserIds) {
      if (userId && vaadMonthlyData[userId]) {
        vaadAmount = vaadMonthlyData[userId];
        break;
      }
    }

    if (!vaadAmount && tenant.email) {
      const emailKey = tenant.email.toLowerCase();
      if (vaadMonthlyData[emailKey]) {
        vaadAmount = vaadMonthlyData[emailKey];
      }
    }

    if (!vaadAmount && tenant.fullName) {
      const nameKey = tenant.fullName.toLowerCase();
      if (vaadMonthlyData[nameKey]) {
        vaadAmount = vaadMonthlyData[nameKey];
      }
    }

    if (vaadAmount && vaadAmount > 0) {
      setAmount(vaadAmount.toString());
    }

    setIsTenantDropdownOpen(false);
    setTenantSearchTerm("");
  };

  useEffect(() => {
    if (apartmentNumber && paymentYear) {
      const tenantEmail = selectedTenant?.email || null;

      const paidMonths = calculateAlreadyPaidMonths(
        tenantEmail,
        apartmentNumber,
        paymentYear,
        paymentType
      );

      setAlreadyPaidMonths(paidMonths);

      if (paymentType === "one_time") {
        const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
        setSelectedMonths([currentMonth]);
      } else {
        setSelectedMonths(paidMonths);
      }
    } else {
      // Clear paid months if we don't have apartment or year
      setAlreadyPaidMonths([]);
    }
  }, [apartmentNumber, paymentYear, selectedTenant, groupedPayments, paymentType]);

  // Additional useEffect to ensure alreadyPaidMonths are always included in selectedMonths
  // BUT: If payment type is "one_time", skip this and only keep current month selected
  useEffect(() => {
    if (paymentType === "one_time") {
      return;
    }

    if (alreadyPaidMonths.length > 0) {
      setSelectedMonths(prev => {
        const newlySelected = prev.filter(month => !alreadyPaidMonths.includes(month));
        const combined = [...new Set([...alreadyPaidMonths, ...newlySelected])].sort();
        return combined;
      });
    }
  }, [alreadyPaidMonths, paymentType]);

  const filteredUsers = users.filter(user => {
    if (!tenantSearchTerm) return true;
    const searchLower = tenantSearchTerm.toLowerCase();
    return (
      user.fullName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.apartmentNumber?.toString().includes(tenantSearchTerm)
    );
  }).sort((a, b) => {
    // Sort by apartment number (convert to number for proper numerical sorting)
    const apartmentA = parseInt(a.apartmentNumber) || 0;
    const apartmentB = parseInt(b.apartmentNumber) || 0;
    return apartmentA - apartmentB;
  });

  const handleTenantDropdownToggle = () => {
    setIsTenantDropdownOpen(!isTenantDropdownOpen);
    if (!isTenantDropdownOpen) {
      setTenantSearchTerm("");
    }
  };

  const resetForm = () => {
    setIsPaymentComplete(false);
    setCompletedPaymentData(null);
    resetFormFields();
    setCurrentView("list");
  };

  // Handle WhatsApp share for completed payment
  const handleCompletedPaymentWhatsApp = async () => {
    if (!completedPaymentData) {
      alert('❌ אין נתוני תשלום זמינים לשיתוף');
      return;
    }

    try {
      // Create a grouped payment object similar to what handleWhatsAppShare expects
      const groupedPayment = {
        months: completedPaymentData.months,
        year: completedPaymentData.year,  // Add year here
        paymentDetails: completedPaymentData.paymentDetails,
        tenantName: completedPaymentData.tenantName,
        tenantId: completedPaymentData.tenantId,
        apartmentNumber: completedPaymentData.apartmentNumber,
        buildingNumber: completedPaymentData.buildingNumber,
        paymentMethod: completedPaymentData.paymentMethod,
        paymentType: completedPaymentData.paymentType, // Add payment type for WhatsApp message
        additionalDetails: completedPaymentData.additionalDetails,
        checkNumber: completedPaymentData.checkNumber,
        email: completedPaymentData.email,
        totalAmount: completedPaymentData.totalAmount,
        monthlyAmount: completedPaymentData.monthlyAmount
      };

      // Use the existing handleWhatsAppShare function
      await handleWhatsAppShare(groupedPayment);
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      alert('❌ שגיאה בשיתוף ל-WhatsApp');
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 2; year >= currentYear - 4; year--) {
      years.push(year);
    }
    return years;
  };

  // UPDATED: Helper functions for button text and processing messages
  const getProcessingMessage = () => {
    const newMonthsCount = getNewlySelectedMonths().length;
    if (newMonthsCount > 1) {
      return `מעבד ${newMonthsCount} תשלומים ויוצר קבלות...`;
    } else {
      return "מעבד תשלום ויוצר קבלה...";
    }
  };

  const getSubmitButtonText = () => {
    if (isProcessing) {
      return getProcessingMessage();
    }

    const newMonthsCount = getNewlySelectedMonths().length;
    if (newMonthsCount > 1) {
      return `צור ${newMonthsCount} תשלומים וקבל קבלות`;
    } else {
      return "שלם עכשיו וקבל קבלה";
    }
  };

  // View Details Modal Component
  const ViewDetailsModal = () => {
    if (!viewModalVisible || !selectedPaymentForView) return null;

    const group = selectedPaymentForView;

    return (
      <div className={styles.modalOverlay} onClick={handleCloseViewModal}>
        <div className={`${styles.modal} ${styles.paymentDetailsModal}`} onClick={(e) => e.stopPropagation()}>
          {/* Enhanced Header with Icon and Gradient */}
          <div className={styles.modalHeader}>
            <div className={styles.modalHeaderContent}>
              <div className={styles.modalTitleSection}>
                <div className={styles.modalIcon}>
                  <Eye size={24} />
                </div>
                <div className={styles.modalTitleText}>
                  <h3 className={styles.modalTitle}>פרטי תשלומים</h3>
                  <p className={styles.modalSubtitle}>דירה {group.apartmentNumber} • {group.year}</p>
                </div>
              </div>
              <button
                onClick={handleCloseViewModal}
                className={styles.modalCloseButton}
                title="סגור"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className={styles.modalContent}>
            {/* Summary Card */}
            <div className={styles.paymentSummaryCard}>
              <div className={styles.summaryHeader}>
                <h4 className={styles.summaryTitle}>סיכום התשלום</h4>
              </div>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <CreditCard size={16} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>סה״כ סכום</span>
                    <span className={styles.summaryValue}>{formatAmount(group.totalAmount)}</span>
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <Calendar size={16} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>חודשים</span>
                    <span className={styles.summaryValue}>{group.months?.length || 0} חודשים</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Details Cards */}
            <div className={styles.detailsCardsGrid}>
              {/* Tenant Information Card */}
              <div className={styles.detailCard}>
                <div className={styles.cardHeader}>
                  <User size={16} />
                  <h5 className={styles.cardTitle}>פרטי דייר</h5>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>שם דייר:</span>
                    <span className={styles.detailValue}>{group.tenantName || 'לא צוין'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>אימייל:</span>
                    <span className={styles.detailValue}>{group.tenantEmail || 'לא צוין'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>דירה:</span>
                    <span className={styles.detailValue}>
                      {group.apartmentNumber}
                      {group.buildingNumber && ` (בניין ${group.buildingNumber})`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Information Card */}
              <div className={styles.detailCard}>
                <div className={styles.cardHeader}>
                  <Receipt size={16} />
                  <h5 className={styles.cardTitle}>פרטי תשלום</h5>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>שנה:</span>
                    <span className={styles.detailValue}>{group.year}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>חודשים שולמו:</span>
                    <span className={styles.detailValue}>
                      {formatMonthsNumeric(group.months, `detail-${group.apartmentNumber}-${group.year}`)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>אמצעי תשלום:</span>
                    <span className={styles.detailValue}>
                      <div className={styles.paymentMethodsContainer}>
                        {group.paymentMethods?.map((method, idx) => (
                          <PaymentMethodBadge key={idx} method={method} />
                        ))}
                      </div>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.modalActions}>
              {group.hasReceipts && (
                <button
                  onClick={() => {
                    handleCloseViewModal();
                    handleViewReceipt(group);
                  }}
                  className={`${styles.actionButton} ${styles.primaryAction}`}
                >
                  <Receipt size={16} />
                  <span>צפה בקבלות</span>
                </button>
              )}

              {group.hasEmailFiles && (
                <button
                  onClick={() => {
                    handleCloseViewModal();
                    handleViewEmail(group);
                  }}
                  className={`${styles.actionButton} ${styles.secondaryAction}`}
                >
                  <Mail size={16} />
                  <span>צפה בקבצי אימייל</span>
                </button>
              )}

              <button
                onClick={() => handleWhatsAppShare(group)}
                className={`${styles.actionButton} ${styles.whatsappAction}`}
              >
                <MessageSquare size={16} />
                <span>שתף ב-WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // UPDATED: Payment success view to reflect multiple payments
  if (isPaymentComplete) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentContainer}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={`${styles.iconCircle} ${styles.green}`}>
                <Check className={styles.iconGreen} />
              </div>
              <h2 className={styles.pageTitle}>
                התשלומים בוצעו בהצלחה!
              </h2>
              <p className={styles.pageSubtitle}>
                קבלות נשלחו לכתובת האימייל שלך ונשמרו במערכת
              </p>
            </div>

            <div className={styles.successDetails}>
              <h3 className={styles.successDetailsTitle}>פרטי תשלומים:</h3>
              <div className={styles.successDetailsContent}>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>שם דייר:</span> {selectedTenant?.fullName}
                </p>
                {tenantId && (
                  <p className={styles.successDetailsItem}>
                    <span className={styles.successDetailsLabel}>ת.ז דייר:</span> {tenantId}
                  </p>
                )}
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>דירה:</span> {apartmentNumber}
                  {buildingNumber && `, בניין ${buildingNumber}`}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>תקופת תשלום:</span> {getNewlySelectedMonthNames()} {paymentYear}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>סוג תשלום:</span> {paymentType === "one_time" ? "תשלום חד פעמי" : "תשלום וועד בית"}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>סכום לחודש:</span> ₪{parseFloat(amount).toLocaleString()}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>מספר חודשים:</span> {getNewlySelectedMonths().length}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>מספר תשלומים שנוצרו:</span> {getNewlySelectedMonths().length}
                </p>
                <p className={styles.successDetailsItem} style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#28a745' }}>
                  <span className={styles.successDetailsLabel}>סכום כולל:</span> ₪{calculateTotalAmount().toLocaleString()}
                </p>
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>אמצעי תשלום:</span> {getPaymentMethodName(paymentMethod)}
                </p>
                {paymentMethod === "check" && checkNumber && (
                  <p className={styles.successDetailsItem}>
                    <span className={styles.successDetailsLabel}>מספר צ'ק:</span> {checkNumber}
                  </p>
                )}
                {additionalPaymentDetails && (
                  <p className={styles.successDetailsItem}>
                    <span className={styles.successDetailsLabel}>פרטים נוספים:</span> {additionalPaymentDetails}
                  </p>
                )}
                <p className={styles.successDetailsItem}>
                  <span className={styles.successDetailsLabel}>אימייל לקבלת חשבוניות:</span> {email}
                </p>
              </div>
            </div>

            <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={resetForm}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                חזרה לרשימת התשלומים
              </button>
              <button
                onClick={() => {
                  setIsPaymentComplete(false);
                  setCurrentView("form");
                  // Reset form data for new payment
                  setSelectedTenant(null);
                  setTenantId("");
                  setApartmentNumber("");
                  setBuildingNumber("");
                  // Payment type is fixed to ועד בית
                  setPaymentMethod("cash");
                  setAmount("");
                  setEmail("");
                  setCheckNumber("");
                  setAdditionalPaymentDetails("");
                  handleNewPayment();
                }}
                className={`${styles.button} ${styles.buttonSecondary}`}
                style={{
                  backgroundColor: "#4F46E5",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <Plus size={20} />
                תשלום חדש
              </button>
              <button
                onClick={handleCompletedPaymentWhatsApp}
                className={`${styles.button} ${styles.whatsappButton}`}
                style={{
                  backgroundColor: "#25D366",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <MessageSquare size={20} />
                שלח ב-WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rest of the component remains the same - List view and form view
  if (currentView === "list") {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.listContainer}>
          <div className={styles.header}>
            <h1>ניהול תשלומים</h1>
            <div className={styles.totalCounts}>
              <span className={styles.countBadge}>
                <List className={styles.countIcon} />
                {groupedPayments.length} קבוצות תשלום
              </span>
              <span className={styles.countBadge}>
                <span className={styles.countIcon}>₪</span>
                {payments.length} תשלומים סה"כ
              </span>
              <span className={styles.countBadge}>
                <Receipt className={styles.countIcon} />
                {groupedPayments.filter(g => g.hasReceipts).length} עם קבלות
              </span>
              <span className={styles.countBadge}>
                <Mail className={styles.countIcon} />
                {groupedPayments.filter(g => g.hasEmailFiles).length} עם קבצי אימייל
              </span>
            </div>
          </div>

          {/* Actions Section - Similar to Message page */}
          <div className={styles.actions}>
            <div className={styles.filtersContainer}>
              {/* Search Button - Opens modal for both desktop and mobile */}
              <button
                className={styles.searchButton}
                onClick={() => setShowMobileSearch(true)}
                title="חיפוש וסינון"
              >
                <Search size={20} />
                <span>חיפוש וסינון</span>
                {(searchTerm || selectedApartmentFilter || showOneTimePaymentsOnly || showUnpaidOnly || showUnpaidOneTimeOnly) && (
                  <span className={styles.activeFiltersIndicator}>
                    {[searchTerm, selectedApartmentFilter, showOneTimePaymentsOnly, showUnpaidOnly, showUnpaidOneTimeOnly].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
            
            {/* Action Buttons Group */}
            <div className={styles.actionButtonsGroup}>
              <button className={styles.addButton} onClick={handleNewPayment}>
                <Plus size={20} />
                הוסף תשלום
              </button>

              {/* All other actions in dropdown */}
              <div className={styles.actionsDropdownContainer} ref={exportDropdownRef}>
                <button
                  className={styles.actionsDropdownButton}
                  onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                >
                  <Menu size={20} />
                  <span>פעולות</span>
                  <ChevronDown 
                    size={16} 
                    className={`${styles.dropdownArrow} ${isExportDropdownOpen ? styles.dropdownArrowOpen : ''}`} 
                  />
                </button>

                {isExportDropdownOpen && (
                  <div className={styles.actionsDropdownMenu}>
                    <button
                      onClick={() => {
                        setShowStatusOverview(true);
                        setIsExportDropdownOpen(false);
                      }}
                      className={styles.dropdownItem}
                    >
                      <Calendar size={18} />
                      הצגה שנתית
                    </button>

                    <div className={styles.menuSeparator}></div>

                    <button
                      onClick={() => {
                        setShowExcelUpload(true);
                        setIsExportDropdownOpen(false);
                      }}
                      className={styles.dropdownItem}
                    >
                      <Upload size={18} />
                      העלה מ-Excel
                    </button>

                    <button
                      onClick={() => {
                        exportToExcel(false);
                        setIsExportDropdownOpen(false);
                      }}
                      disabled={isExporting || currentGroupedPayments.length === 0}
                      className={styles.dropdownItem}
                    >
                      {isExporting ? (
                        <Loader size={18} className={styles.spinning} />
                      ) : (
                        <Download size={18} />
                      )}
                      ייצא עמוד נוכחי
                    </button>

                    <button
                      onClick={() => {
                        exportToExcel(true);
                        setIsExportDropdownOpen(false);
                      }}
                      disabled={isExporting || filteredGroupedPayments.length === 0}
                      className={styles.dropdownItem}
                    >
                      {isExporting ? (
                        <Loader size={18} className={styles.spinning} />
                      ) : (
                        <FileSpreadsheet size={18} />
                      )}
                      ייצא את כל הנתונים
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isLoadingPayments && (
            <div className={styles.loadingContainer}>
              <Loader className={styles.loadingIcon} />
              <span>טוען תשלומים...</span>
            </div>
          )}

          {paymentsError && (
            <div className={styles.errorContainer}>
              <span className={styles.errorText}>{paymentsError}</span>
              <button
                onClick={fetchPayments}
                className={styles.retryButton}
              >
                נסה שוב
              </button>
            </div>
          )}

          {!isLoadingPayments && !paymentsError && (
            <>
              {/* Show users who haven't paid one-time payment */}
              {showUnpaidOneTimeOnly ? (
                <div className={`table-container ${styles.tableContainer}`}>
                  <div className={styles.unpaidOneTimeHeader}>
                    <h3>דיירים שלא שילמו תשלום חד פעמי ({isLoadingUsers ? '...' : usersWithoutOneTimePayment.length})</h3>
                    {/* Action Buttons */}
                    {!isLoadingUsers && usersWithoutOneTimePayment.length > 0 && (
                      <div className={styles.unpaidOneTimeActions}>
                        {/* Print Button */}
                        <button
                          className={styles.printHeaderButton}
                          onClick={printUnpaidOneTimeList}
                          title="הדפס רשימה"
                        >
                          <Printer size={16} />
                          <span>הדפסה</span>
                        </button>

                        {/* WhatsApp Button with Dropdown */}
                        <div className={styles.whatsappDropdownContainer} ref={whatsappOneTimeDropdownRef}>
                          <button
                            className={styles.whatsappHeaderButton}
                            onClick={() => setIsWhatsappOneTimeDropdownOpen(!isWhatsappOneTimeDropdownOpen)}
                            title="שלח רשימה ב-WhatsApp"
                          >
                            <MessageSquare size={16} />
                            <span>שליחה WhatsApp</span>
                            <ChevronDown
                              size={12}
                              className={`${styles.chevronIcon} ${isWhatsappOneTimeDropdownOpen ? styles.rotated : ''}`}
                            />
                          </button>
                          {isWhatsappOneTimeDropdownOpen && (
                            <div className={styles.whatsappDropdownMenu}>
                              <button
                                className={styles.whatsappDropdownItem}
                                onClick={() => {
                                  sendWhatsAppUnpaidOneTimeList(false);
                                  setIsWhatsappOneTimeDropdownOpen(false);
                                }}
                              >
                                <MessageSquare size={16} />
                                <span>ללא מספרי טלפון</span>
                              </button>
                              <button
                                className={styles.whatsappDropdownItem}
                                onClick={() => {
                                  sendWhatsAppUnpaidOneTimeList(true);
                                  setIsWhatsappOneTimeDropdownOpen(false);
                                }}
                              >
                                <MessageSquare size={16} />
                                <span>כולל מספרי טלפון</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {isLoadingUsers ? (
                    <div className={styles.loadingContainer}>
                      <Loader className={styles.spinningLoader} size={32} />
                      <span>טוען רשימת דיירים...</span>
                    </div>
                  ) : (
                    <table className={`table ${styles.usersTable}`}>
                      <thead>
                        <tr>
                          <th>שם דייר</th>
                          <th>דירה</th>
                          <th>טלפון</th>
                          <th>אימייל</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersWithoutOneTimePayment.length === 0 ? (
                          <tr>
                            <td colSpan="4" className={styles.emptyState}>
                              כל הדיירים שילמו תשלום חד פעמי
                            </td>
                          </tr>
                        ) : (
                          usersWithoutOneTimePayment.map((user, index) => (
                            <tr key={user.id || index}>
                              <td data-label="שם דייר">
                                {user.FullName || user.fullName || `${user.FirstName || user.firstName || ''} ${user.LastName || user.lastName || ''}`.trim() || '-'}
                              </td>
                              <td data-label="דירה">
                                {user.ApartmentNumber || user.apartmentNumber}
                              </td>
                              <td data-label="טלפון">
                                {user.Phone || user.phone || '-'}
                              </td>
                              <td data-label="אימייל">
                                {user.Email || user.email || '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
              <>
              <div className={`table-container ${styles.tableContainer}`}>
                <table className={`table ${styles.usersTable}`}>
                  <thead>
                    <tr>
                      <th>שנה</th>
                      <th>שם דייר</th>
                      <th>דירה</th>
                      <th>חודשים שולמו</th>
                      <th>סה״כ סכום</th>
                      <th>אמצעי תשלום</th>
                      <th>קבלות</th>
                      <th>קבצי אימייל</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentGroupedPayments.length === 0 ? (
                      <tr>
                        <td colSpan="9" className={styles.emptyState}>
                          {searchTerm ? "לא נמצאו תוצאות" : "לא נמצאו תשלומים"}
                        </td>
                      </tr>
                    ) : (
                      currentGroupedPayments.map((group, index) => (
                        <tr key={`${group.apartmentNumber}-${group.year}`}>
                          <td data-label="שנה">{group.year}</td>
                          <td data-label="שם דייר">
                            {group.tenantName || group.tenantEmail}
                          </td>
                          <td data-label="דירה">
                            {group.apartmentNumber}
                            {group.buildingNumber && ` (בניין ${group.buildingNumber})`}
                          </td>
                          <td data-label="חודשים שולמו">
                            <span className={styles.monthsBadge}>
                              {formatMonthsNumeric(group.months, `table-${group.apartmentNumber}-${group.year}`)}
                            </span>
                          </td>
                          <td data-label="סה״כ סכום">{formatAmount(group.totalAmount)}</td>
                          <td data-label="אמצעי תשלום">
                            <div className={styles.paymentMethodsContainer}>
                              {group.paymentMethods.map((method, idx) => (
                                <PaymentMethodBadge key={idx} method={method} />
                              ))}
                            </div>
                          </td>
                          <td data-label="קבלות">
                            {group.hasReceipts ? (
                              <span className={`table-badge table-badge-success ${styles.hasReceiptsBadge}`}>
                                <Receipt className={styles.badgeIcon} />
                                יש קבלות
                              </span>
                            ) : (
                              <span className={`table-badge table-badge-neutral ${styles.noReceiptsBadge}`}>
                                אין קבלות
                              </span>
                            )}
                          </td>
                          <td data-label="קבצי אימייל">
                            {group.hasEmailFiles ? (
                              <span className={`table-badge table-badge-info ${styles.hasEmailFilesBadge}`}>
                                <Mail className={styles.badgeIcon} />
                                יש קבצי אימייל
                              </span>
                            ) : (
                              <span className={`table-badge table-badge-neutral ${styles.noEmailFilesBadge}`}>
                                אין קבצי אימייל
                              </span>
                            )}
                          </td>
                          <td data-label="פעולות" className={styles.actionsCell}>
                            <ActionMenu
                              group={group}
                              onViewDetails={handleViewDetails}
                              onEditPayment={handleEditPayment}
                              onViewReceipt={handleViewReceipt}
                              onViewEmail={handleViewEmail}
                              onWhatsAppShare={handleWhatsAppShare}
                              loading={isLoadingPayments}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.mobileCardsContainer}>
                <div className={styles.cardsGrid}>
                  {currentGroupedPayments.length === 0 ? (
                    <div className={styles.emptyState}>
                      {searchTerm ? "לא נמצאו תוצאות" : "לא נמצאו תשלומים"}
                    </div>
                  ) : (
                    currentGroupedPayments.map((group, index) => (
                      <MobilePaymentCard
                        key={`${group.apartmentNumber}-${group.year}`}
                        group={group}
                        onViewDetails={handleViewDetails}
                        onEditPayment={handleEditPayment}
                        onViewReceipt={handleViewReceipt}
                        onViewEmail={handleViewEmail}
                        onWhatsAppShare={handleWhatsAppShare}
                        formatAmount={formatAmount}
                        formatMonthsNumeric={formatMonthsNumeric}
                      />
                    ))
                  )}
                </div>
              </div>
              </>
              )}

              {!showUnpaidOneTimeOnly && filteredGroupedPayments.length > 0 && (
                <div className={styles.pagination}>
                  <div className={styles.paginationControls}>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={styles.paginationButton}
                      title="עמוד קודם"
                    >
                      <ChevronRight />
                    </button>
                    <span className={styles.pageInfo}>
                      עמוד {currentPage} מתוך {totalPages}
                    </span>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={styles.paginationButton}
                      title="עמוד הבא"
                    >
                      <ChevronLeft />
                    </button>
                  </div>


                  <div className={styles.resultsPerPage}>
                    <span>תוצאות בעמוד:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  <div className={styles.totalInfo}>
                    מציג {indexOfFirstPayment + 1}-{Math.min(indexOfLastPayment, filteredGroupedPayments.length)} מתוך {filteredGroupedPayments.length} קבוצות תשלום
                  </div>
                </div>
              )}
            </>
          )}

          <div className={styles.footer}>
            <p className={styles.footerText}>
              © {new Date().getFullYear()} ניהול ועד בית - כל הזכויות שמורות
            </p>
          </div>
        </div>

        <ViewDetailsModal />
        <ReceiptModal />
        <EmailFilesModal />

        {/* One-Time Payment Warning Modal */}
        {showOneTimeWarning && (
          <div className={styles.modalOverlay} onClick={handleCancelOneTimePayment}>
            <div className={`${styles.modal} ${styles.warningModal}`} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>⚠️ תשלום חד פעמי קיים</h2>
                <button
                  className={styles.modalCloseBtn}
                  onClick={handleCancelOneTimePayment}
                  aria-label="סגור"
                >
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <p className={styles.warningText}>
                  קיים כבר תשלום חד פעמי עבור דירה זו. להלן פרטי התשלומים הקודמים:
                </p>
                <div className={styles.existingPaymentsList}>
                  {existingOneTimePayments.map((payment, index) => (
                    <div key={payment.paymentId || index} className={styles.existingPaymentItem}>
                      <div className={styles.existingPaymentRow}>
                        <span className={styles.existingPaymentLabel}>תאריך:</span>
                        <span className={styles.existingPaymentValue}>
                          {new Date(payment.date).toLocaleDateString('he-IL')}
                        </span>
                      </div>
                      <div className={styles.existingPaymentRow}>
                        <span className={styles.existingPaymentLabel}>סכום:</span>
                        <span className={styles.existingPaymentValue}>₪{payment.amount?.toLocaleString()}</span>
                      </div>
                      <div className={styles.existingPaymentRow}>
                        <span className={styles.existingPaymentLabel}>אמצעי תשלום:</span>
                        <span className={styles.existingPaymentValue}>{payment.paymentMethod}</span>
                      </div>
                      {payment.tenantName && (
                        <div className={styles.existingPaymentRow}>
                          <span className={styles.existingPaymentLabel}>שם הדייר:</span>
                          <span className={styles.existingPaymentValue}>{payment.tenantName}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className={styles.warningQuestion}>
                  האם אתה בטוח שברצונך להוסיף תשלום חד פעמי נוסף?
                </p>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={handleCancelOneTimePayment}
                >
                  ביטול
                </button>
                <button
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  onClick={handleConfirmOneTimePayment}
                >
                  המשך בתשלום
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Excel Upload Modal */}
        {showExcelUpload && (
          <PaymentExcelUpload
            onClose={() => setShowExcelUpload(false)}
            onPaymentsCreated={() => {
              setShowExcelUpload(false);
              fetchPayments(); // Refresh payments list after upload
            }}
            buildingId={buildingId || getBuildingId()}
          />
        )}

        {showStatusOverview && (
          <PaymentStatusOverview
            onClose={() => setShowStatusOverview(false)}
            buildingId={buildingId || getBuildingId()}
          />
        )}

        {/* Mobile Search Button - Rendered outside main container */}
        {!showMobileSearch && createPortal(
          <button
            className={styles.mobileSearchButton}
            onClick={() => setShowMobileSearch(true)}
            aria-label="פתח חיפוש"
          >
            <Search size={22} />
            {(searchTerm || selectedApartmentFilter || showOneTimePaymentsOnly || showUnpaidOnly || showUnpaidOneTimeOnly) && (
              <span className={styles.searchIndicatorDot}></span>
            )}
          </button>,
          document.body
        )}

        {/* Mobile Search Modal */}
        {showMobileSearch && createPortal(
          <div className={styles.mobileSearchModal}>
            <div className={styles.mobileSearchHeader}>
              <h3>חיפוש וסינון</h3>
              <button className={styles.mobileSearchClose} onClick={() => setShowMobileSearch(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.mobileSearchContent}>
              <div className={styles.mobileSearchField}>
                <Search size={20} className={styles.mobileSearchIcon} />
                <input
                  type="text"
                  placeholder="חפש לפי שם דייר, סוג תשלום או סכום..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className={styles.mobileSearchField}>
                <Home size={20} className={styles.mobileSearchIcon} />
                <input
                  type="text"
                  placeholder="הזן מספר דירה"
                  value={selectedApartmentFilter}
                  onChange={(e) => setSelectedApartmentFilter(e.target.value)}
                />
              </div>
              <label className={styles.mobileCheckboxFilter}>
                <input
                  type="checkbox"
                  checked={showOneTimePaymentsOnly}
                  onChange={(e) => setShowOneTimePaymentsOnly(e.target.checked)}
                />
                <span>תשלומים חד פעמיים בלבד</span>
              </label>
              <label className={styles.mobileCheckboxFilter}>
                <input
                  type="checkbox"
                  checked={showUnpaidOnly}
                  onChange={(e) => setShowUnpaidOnly(e.target.checked)}
                />
                <span>דירות שלא שילמו בחודש הנוכחי</span>
              </label>
              <label className={styles.mobileCheckboxFilter}>
                <input
                  type="checkbox"
                  checked={showUnpaidOneTimeOnly}
                  onChange={(e) => setShowUnpaidOneTimeOnly(e.target.checked)}
                />
                <span>דיירים שלא שילמו תשלום חד פעמי</span>
              </label>
              {(searchTerm || selectedApartmentFilter || showOneTimePaymentsOnly || showUnpaidOnly || showUnpaidOneTimeOnly) && (
                <button
                  className={styles.mobileSearchClear}
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedApartmentFilter('');
                    setShowOneTimePaymentsOnly(false);
                    setShowUnpaidOnly(false);
                    setShowUnpaidOneTimeOnly(false);
                  }}
                >
                  נקה הכל
                </button>
              )}
              <button className={styles.mobileSearchApply} onClick={() => setShowMobileSearch(false)}>
                החל
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Payment Form View - UPDATED with new submit button text
  if (currentView === "form" || currentView === "edit") {
    const isEditMode = currentView === "edit";

    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentContainer}>
          <div className={styles.card}>
            <div className={styles.backButtonContainer}>
              <button
                onClick={() => isEditMode ? handleCancelEdit() : setCurrentView("list")}
                className={styles.backButton}
              >
                <ChevronRight className={styles.iconLeft} />
                {isEditMode ? "בטל עריכה" : "חזרה לרשימת התשלומים"}
              </button>
            </div>

            <div className={styles.header}>
              <div className={`${styles.iconCircle} ${styles.blue}`}>
                <Building className={styles.iconBlue} />
              </div>
              <h1 className={styles.pageTitle}>
                {isEditMode ? "עריכת תשלום" : "תשלום חדש לוועד בית"}
              </h1>
              <p className={styles.pageSubtitle}>
                {isEditMode ? "ערוך את פרטי התשלום" : "מלא את הפרטים כדי לבצע תשלום ולקבל קבלה"}
              </p>
            </div>

            {/* Tenant Selection */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                {isEditMode ? "דייר" : "בחר דייר"} *
              </label>
              <div className={styles.formInputContainer}>
                <div className={styles.dropdownButton} onClick={handleTenantDropdownToggle}>
                  <span className={styles.dropdownButtonText}>
                    {selectedTenant ?
                      `${selectedTenant.fullName} - דירה ${selectedTenant.apartmentNumber}` :
                      "בחר דייר..."
                    }
                  </span>
                  <ChevronDown className={styles.dropdownArrow} />
                </div>

                {isTenantDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <div style={{ padding: '0.5rem' }}>
                      <input
                        ref={tenantSearchInputRef}
                        type="text"
                        value={tenantSearchTerm}
                        onChange={(e) => setTenantSearchTerm(e.target.value)}
                        className={styles.searchInput}
                        placeholder="חפש דייר..."
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className={styles.dropdownResults}>
                      {isLoadingUsers ? (
                        <div key="loading" className={styles.loadingContainer}>
                          <Loader className={styles.loadingIcon} />
                          <span>טוען דיירים...</span>
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div key="empty" className={styles.emptyState}>לא נמצאו דיירים</div>
                      ) : (
                        filteredUsers.map((user, index) => (
                          <div
                            key={`user-${user.userId || user.email || index}`}
                            className={`${styles.dropdownItem} ${selectedTenant?.userId === user.userId ? styles.selected : ''}`}
                            onClick={() => handleTenantSelection(user)}
                          >
                            <div className={styles.tenantDropdownItem}>
                              <div className={styles.tenantInfo}>
                                <div className={styles.tenantName}>{user.fullName}</div>
                                <div className={styles.tenantDetails}>
                                  {user.email} • דירה {user.apartmentNumber}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {usersError && (
                <span className={styles.errorText}>{usersError}</span>
              )}
            </div>

            {/* Payment Period Selection */}
            <div className={styles.cardSection}>
              <h3 style={{ marginBottom: '1rem', color: '#374151' }}>תקופת התשלום</h3>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>סוג תשלום *</label>
                  <select
                    value={paymentType}
                    onChange={(e) => handlePaymentTypeChange(e.target.value)}
                    className={styles.formInput}
                    required
                  >
                    <option value="regular">תשלום וועד בית</option>
                    <option value="one_time">תשלום חד פעמי</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>שנת תשלום *</label>
                  <select
                    value={paymentYear}
                    onChange={(e) => setPaymentYear(e.target.value)}
                    className={styles.formInput}
                    required
                  >
                    {generateYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.monthSelectionHeader}>
                <label className={styles.formLabel}>חודשים לתשלום *</label>
                <button
                  type="button"
                  onClick={handleSelectAllMonths}
                  className={styles.selectAllButton}
                  disabled={isProcessing}
                >
                  {(() => {
                    const unpaidMonths = months.filter(month => !alreadyPaidMonths.includes(month.value));
                    const newlySelected = getNewlySelectedMonths();
                    return newlySelected.length === unpaidMonths.length ? "בטל בחירת חודשים חדשים" : "בחר כל החודשים הפנויים";
                  })()}
                </button>
              </div>

              <div className={styles.monthCheckboxGrid}>
                {months.map((month) => {
                  const isPaid = alreadyPaidMonths.includes(month.value);
                  const isChecked = selectedMonths.includes(month.value);
                  
                  return (
                    <div
                      key={month.value}
                      className={`${styles.monthCheckboxItem} ${isPaid ? styles.monthPaid : ''}`}
                    >
                      <label className={styles.monthCheckboxLabel}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleMonthToggle(month.value)}
                          className={`${styles.monthCheckbox} ${isPaid ? styles.monthCheckboxPaid : ''}`}
                          disabled={isProcessing || isPaid || (isEditMode && !isPaid)} // In edit mode, only allow editing paid months
                        />
                        <span className={`${styles.monthLabelText} ${isPaid ? styles.monthLabelPaid : ''}`}>
                          {month.label}
                          {isPaid && (
                            <span className={styles.paidIndicator}> (שולם)</span>
                          )}
                        </span>
                      </label>
                      {isPaid && (
                        <div className={styles.monthActionButtons}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteMonthPaymentDirect(month.value, month.label);
                            }}
                            className={styles.deletePaymentButton}
                            title={`מחק תשלום עבור ${month.label}`}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedMonths.length > 0 && (
                <div className={styles.selectedMonthsDisplay}>
                  {(alreadyPaidMonths.length > 0 || getNewlySelectedMonths().length > 0) && (
                    <div>
                      {alreadyPaidMonths.length > 0 && (
                        <div className={styles.alreadyPaidMonthsDisplay}>
                          <p className={styles.alreadyPaidMonthsText}>
                            <strong>חודשים ששולמו כבר:</strong> {
                              alreadyPaidMonths
                                .map(value => months.find(month => month.value === value)?.label)
                                .filter(Boolean)
                                .join(', ')
                            }
                          </p>
                        </div>
                      )}

                      {getNewlySelectedMonths().length > 0 && (
                        <div className={styles.selectedMonthsDisplay}>
                          <p className={styles.selectedMonthsText}>
                            <strong>חודשים חדשים לתשלום:</strong> {getNewlySelectedMonthNames()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Type - Fixed to ועד בית */}

            {/* Amount */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                סכום *
                {selectedTenant && (() => {
                  let foundAmount = null;

                  const possibleUserIds = [
                    selectedTenant.userID,
                    selectedTenant.userId,
                    selectedTenant.UserID,
                    selectedTenant.userid,
                    selectedTenant.id,
                    selectedTenant.Id,
                    selectedTenant.ID
                  ];

                  for (const userId of possibleUserIds) {
                    if (userId && vaadMonthlyData[userId]) {
                      foundAmount = vaadMonthlyData[userId];
                      break;
                    }
                  }

                  if (!foundAmount && selectedTenant.email) {
                    foundAmount = vaadMonthlyData[selectedTenant.email.toLowerCase()];
                  }

                  if (!foundAmount && selectedTenant.fullName) {
                    foundAmount = vaadMonthlyData[selectedTenant.fullName.toLowerCase()];
                  }

                  return foundAmount && foundAmount > 0 ? (
                    <span className={styles.autoPopulatedHint}>
                      (הסכום נטען אוטומטית מתשלום החודשי: ₪{foundAmount})
                    </span>
                  ) : null;
                })()}
              </label>
              <div className={styles.formInputContainer}>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className={`${styles.formInput} ${selectedTenant && (() => {
                    let hasAmount = false;

                    const possibleUserIds = [
                      selectedTenant.userID,
                      selectedTenant.userId,
                      selectedTenant.UserID,
                      selectedTenant.userid,
                      selectedTenant.id,
                      selectedTenant.Id,
                      selectedTenant.ID
                    ];

                    for (const userId of possibleUserIds) {
                      if (userId && vaadMonthlyData[userId] && vaadMonthlyData[userId] > 0) {
                        hasAmount = true;
                        break;
                      }
                    }

                    if (!hasAmount && selectedTenant.email && vaadMonthlyData[selectedTenant.email.toLowerCase()]) {
                      hasAmount = true;
                    }

                    if (!hasAmount && selectedTenant.fullName && vaadMonthlyData[selectedTenant.fullName.toLowerCase()]) {
                      hasAmount = true;
                    }

                    return hasAmount ? styles.autoPopulated : '';
                  })()}`}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
                <div className={styles.formInputIcon}>
                  <span className={styles.shekelIcon}>₪</span>
                </div>
              </div>
              {isLoadingVaadMonthly && (
                <span className={styles.loadingText}>טוען נתוני תשלום חודשי...</span>
              )}
              {vaadMonthlyError && (
                <span className={styles.errorText}>{vaadMonthlyError}</span>
              )}
              {selectedTenant && !isLoadingVaadMonthly && (() => {
                let hasAmount = false;

                const possibleUserIds = [
                  selectedTenant.userID,
                  selectedTenant.userId,
                  selectedTenant.UserID,
                  selectedTenant.userid,
                  selectedTenant.id,
                  selectedTenant.Id,
                  selectedTenant.ID
                ];

                for (const userId of possibleUserIds) {
                  if (userId && vaadMonthlyData[userId] && vaadMonthlyData[userId] > 0) {
                    hasAmount = true;
                    break;
                  }
                }

                if (!hasAmount && selectedTenant.email && vaadMonthlyData[selectedTenant.email.toLowerCase()]) {
                  hasAmount = true;
                }

                if (!hasAmount && selectedTenant.fullName && vaadMonthlyData[selectedTenant.fullName.toLowerCase()]) {
                  hasAmount = true;
                }

                return !hasAmount && (
                  <span className={styles.warningText}>
                    לא נמצא תשלום חודשי עבור דייר זה - אנא הזן סכום ידנית
                  </span>
                );
              })()}
            </div>
            {amount && getNewlySelectedMonths().length > 0 && (
              <div className={styles.totalAmountDisplay}>
                <div className={styles.totalAmountCard}>
                  <h4 className={styles.totalAmountTitle}>סיכום תשלום</h4>
                  <div className={styles.totalAmountBreakdown}>
                    <div className={styles.amountRow}>
                      <span className={styles.amountLabel}>סכום לחודש:</span>
                      <span className={styles.amountValue}>₪{parseFloat(amount).toLocaleString('he-IL')}</span>
                    </div>
                    <div className={styles.amountRow}>
                      <span className={styles.amountLabel}>מספר חודשים:</span>
                      <span className={styles.amountValue}>{getNewlySelectedMonths().length}</span>
                    </div>
                    <div className={styles.amountRow}>
                      <span className={styles.amountLabel}>חודשים נבחרים:</span>
                      <span className={styles.amountValue}>{getNewlySelectedMonthNames()}</span>
                    </div>
                    <hr className={styles.totalDivider} />
                    <div className={styles.amountRow} style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      <span className={styles.amountLabel}>סכום כולל:</span>
                      <span className={styles.amountValue} style={{ color: '#28a745' }}>
                        ₪{calculateTotalAmount().toLocaleString('he-IL')}
                      </span>
                    </div>
                    {!isEditMode && getNewlySelectedMonths().length > 1 && (
                      <div className={styles.amountRow} style={{ fontSize: '0.9rem', color: '#6b7280', fontStyle: 'italic' }}>
                        <span className={styles.amountLabel}>תשלומים שייווצרו:</span>
                        <span className={styles.amountValue}>{getNewlySelectedMonths().length} רשומות נפרדות</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* UPDATED Payment Method - NEW DROPDOWN */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>אמצעי תשלום *</label>
              <div className={styles.formInputContainer}>
                <div className={styles.dropdownButton} onClick={() => setIsPaymentMethodDropdownOpen(!isPaymentMethodDropdownOpen)}>
                  <span className={styles.dropdownButtonText}>
                    {getPaymentMethodName(paymentMethod)}
                  </span>
                  <ChevronDown className={styles.dropdownArrow} />
                </div>

                {isPaymentMethodDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`${styles.dropdownItem} ${paymentMethod === method.id ? styles.selected : ''}`}
                        onClick={() => {
                          setPaymentMethod(method.id);
                          setIsPaymentMethodDropdownOpen(false);
                        }}
                      >
                        <span className={styles.dropdownItemText}>{method.name}</span>
                        {paymentMethod === method.id && (
                          <Check className={styles.dropdownItemCheck} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Check Number Field - Show only when payment method is check */}
            {paymentMethod === "check" && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>מספר צ'ק *</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  placeholder="הזן מספר צ'ק"
                  required={paymentMethod === "check"}
                />
              </div>
            )}

            {/* Email */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>כתובת אימייל לקבלת חשבונית</label>
              <div className={styles.formInputContainer}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.formInput}
                  placeholder="example@email.com"
                />
                <div className={styles.formInputIcon}>
                  <Mail size={16} />
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>פרטים נוספים</label>
              <textarea
                value={additionalPaymentDetails}
                onChange={(e) => setAdditionalPaymentDetails(e.target.value)}
                className={styles.formTextarea}
                rows="3"
                placeholder="הערות או פרטים נוספים..."
              />
            </div>

            {/* UPDATED Submit Button - uses new helper functions */}
            <div>
              <button
                type="button"
                onClick={isEditMode ? handleUpdatePayment : handlePreSubmit}
                disabled={isProcessing ||
                  !apartmentNumber ||
                  (isEditMode ? selectedMonths.length === 0 : (paymentType === "one_time" ? selectedMonths.length === 0 : getNewlySelectedMonths().length === 0)) ||
                  !paymentYear ||
                  !amount ||
                  (!isEditMode && !selectedTenant)}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                {isEditMode ?
                  (isProcessing ? "מעדכן תשלום..." : "עדכן תשלום") :
                  getSubmitButtonText()
                }
              </button>
            </div>

            <div className={styles.securityNote}>
              <FileText className={styles.securityNoteIcon} />
              <span>פרטי התשלום מאובטחים ומוצפנים. קבלה תישמר במערכת כקובץ אימייל</span>
            </div>
          </div>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              © {new Date().getFullYear()} ניהול ועד בית - כל הזכויות שמורות
            </p>
          </div>
        </div>

        {/* Excel Upload Modal */}
        {showExcelUpload && (
          <PaymentExcelUpload
            onClose={() => setShowExcelUpload(false)}
            onPaymentsCreated={() => {
              setShowExcelUpload(false);
              fetchPayments(); // Refresh payments list after upload
            }}
            buildingId={buildingId || getBuildingId()}
          />
        )}

        {showStatusOverview && (
          <PaymentStatusOverview
            onClose={() => setShowStatusOverview(false)}
            buildingId={buildingId || getBuildingId()}
          />
        )}

        {/* Success Animation */}
        {showSuccessAnimation && (
          <div className={styles.successOverlay}>
            <div className={styles.successCircle}>
              <svg className={styles.successCheckmark} viewBox="0 0 52 52">
                <path className={styles.checkmarkPath} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          </div>
        )}

      </div>
    );
  }

  return null;
}